import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CalendarDays, ListFilter, Star } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { api, getApiErrorMessage } from '../lib/api'
import { unwrapAsyncResult } from '../lib/asyncOperation'
import {
  downloadUtf8BomFile,
  excelCsvPhoneCell,
  stringifyCsvRow,
} from '../lib/customerCsv'
import {
  CUSTOMER_SPREADSHEET_EXPORT_HEADERS,
  mapCustomerImportRow,
  parseCustomerImportCsv,
  readCustomerImportCsvFileAsText,
  validateCustomerImportHeaders,
} from '../lib/customerSpreadsheetImport'
import {
  formatBirthYmdInput,
  formatKoreanPhoneInput,
  normalizeBirthDateForApi,
  normalizeBirthDateForCsvImport,
  restoreLeadingZeroKoreanPhoneDigits,
} from '../lib/crmInputFormat'
import { patchCreateFormFromExtracted } from '../lib/customerBusinessCardExtract'
import { CUSTOMER_SEGMENT_OPTIONS, CUSTOMER_SOURCE_OPTIONS, segmentOptionsForStored, sourceOptionsForStored } from '../lib/customerCrmOptions'
import { customerMemoDraftKey } from '../lib/customerMemoDraftStorage'
import { applyCustomerMemoTemplateVariables, buildCustomerMemoTemplateVariables, chooseCustomerMemoTemplateApplyMode, hasCustomerMemoDraftContent, mergeCustomerMemoTemplateDraft, recordCustomerMemoTemplateUsage } from '../lib/customerMemoTemplates'
import MenuGuideButton from '../components/MenuGuideButton'
import CustomerMemoRichEditor, { memoContentToHtml } from '../components/CustomerMemoRichEditor'
import CustomerMemoTemplatePicker from '../components/CustomerMemoTemplatePicker'
import CustomerListFilterDialog, {
  activeCustomerListFilterCount,
  customerListFilterParams,
  DEFAULT_CUSTOMER_AGE_BAND_OPTIONS,
  EMPTY_CUSTOMER_LIST_FILTERS,
} from '../components/CustomerListFilterDialog'
import { Button } from '../components/ui/button'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'
import { PageFrame, PageHeader, ToolbarSurface, WorkSurface } from '../ui/PageLayout'

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function incomeManToWon(value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (!digits) return 0
  return Number(digits) * 10000
}

function customerNamePhoneLabel(item) {
  const name = String(item?.customerName || '').trim() || '이름 없음'
  const phone = formatKoreanPhoneInput(item?.customerPhone || '')
  return phone ? `${name}(${phone})` : name
}
const CRM_HEADER_BTN =
  'inline-flex h-8 items-center justify-center rounded-md border px-3 text-xs font-medium shadow-sm transition-colors'
const CRM_HEADER_BTN_SIZE = 'h-8 rounded-md px-3 text-xs font-medium'
const CUSTOMER_QUICK_FILTERS = [
  { value: 'all', label: '전체' },
  { value: 'favorites', label: '즐겨찾기' },
]

function mergeCustomerFilterOptions(rows, fallback = []) {
  const merged = [...(Array.isArray(fallback) ? fallback : []), ...(Array.isArray(rows) ? rows : [])]
  const seen = new Set()
  return merged.reduce((options, item) => {
    const value = String(item?.value || '').trim()
    if (!value || seen.has(value)) return options
    seen.add(value)
    options.push({ value, label: String(item?.label || value).trim() || value })
    return options
  }, [])
}

const INITIAL_CUSTOMER_FILTER_OPTIONS = {
  regions: [],
  ageBands: DEFAULT_CUSTOMER_AGE_BAND_OPTIONS,
  sources: CUSTOMER_SOURCE_OPTIONS.filter((option) => option.value),
  segments: CUSTOMER_SEGMENT_OPTIONS.filter((option) => option.value),
}

function toCustomerId(value) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

function customerIsFavorite(row) {
  return row?.isFavorite === true
}
function pad2(n) {
  return String(n).padStart(2, '0')
}

/** @returns {{ startLocal: string, endLocal: string }} datetime-local 값 */
function defaultScheduleRangeLocal() {
  const start = new Date()
  start.setMinutes(0, 0, 0)
  start.setHours(start.getHours() + 1)
  const end = new Date(start.getTime() + 60 * 60 * 1000)
  const toLocal = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
  return { startLocal: toLocal(start), endLocal: toLocal(end) }
}

function toDatetimeLocal(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

function defaultContactAtLocal() {
  const now = new Date()
  now.setSeconds(0, 0)
  return toDatetimeLocal(now)
}

function customerRefQuery(id) {
  if (id == null) return ''
  return `customerRefId=${encodeURIComponent(String(id))}`
}

function formatYmd(v) {
  if (v == null || v === '') return ''
  const s = String(v).trim()
  return s.length >= 10 ? s.slice(0, 10) : s
}

function openDaumPostcode(onComplete) {
  if (!window?.daum?.Postcode) {
    throw new Error('daum_postcode_not_loaded')
  }
  new window.daum.Postcode({
    oncomplete(data) {
      onComplete?.(data)
    },
  }).open()
}

function formatDaumCustomerAddress(data) {
  const zone = String(data?.zonecode || '').trim()
  const road = String(data?.roadAddress || data?.autoRoadAddress || '').trim()
  const jibun = String(data?.jibunAddress || data?.autoJibunAddress || data?.address || '').trim()
  const buildingName = String(data?.buildingName || '').trim()
  const baseAddress = road || jibun
  return [
    zone ? '(' + zone + ')' : '',
    baseAddress,
    buildingName && !baseAddress.includes(buildingName) ? buildingName : '',
  ].filter(Boolean).join(' ').trim()
}

function RequiredMark() {
  return <span className="text-rose-600" aria-hidden="true">*</span>
}

function CreateCustomerYmdInput({ value, onChange, disabled, placeholder, ariaLabel }) {
  const normalized = normalizeBirthDateForApi(value)
  const calendarValue = normalized.ok && normalized.value ? normalized.value : ''
  const calendarLabel = [ariaLabel || '날짜', '달력'].join(' ')
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_2.75rem] gap-1">
      <input
        type="text"
        className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm tabular-nums"
        value={value}
        onChange={(e) => onChange(formatBirthYmdInput(e.target.value))}
        placeholder={placeholder || 'YYYY-MM-DD'}
        inputMode="numeric"
        disabled={disabled}
      />
      <div
        className={'relative flex min-w-0 items-center justify-center rounded border border-slate-300 transition focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-100 ' + (disabled ? 'bg-slate-100 text-slate-400' : 'bg-white text-slate-600 hover:bg-slate-50')}
        title={calendarLabel}
      >
        <CalendarDays className="h-4 w-4" aria-hidden="true" />
        <input
          type="date"
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          value={calendarValue}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-label={calendarLabel}
        />
      </div>
    </div>
  )
}

function optionDisplayLabel(options, value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const found = (Array.isArray(options) ? options : []).find((opt) => String(opt.value || '') === raw)
  return found?.label || raw
}

function compactCustomerDate(value) {
  const formatted = formatYmd(value)
  return formatted || ''
}

function customerSortHeadline(row, sortOption) {
  const sortKey = String(sortOption || '')
  if (sortKey.startsWith('lastContactAt')) {
    const value = compactCustomerDate(row?.lastContactAtDisplay || row?.lastContactAt)
    return value ? `최근 연락 ${value}` : ''
  }
  if (sortKey.startsWith('latestContractDate')) {
    const value = compactCustomerDate(row?.latestContractDate)
    return value ? `최근 계약 ${value}` : ''
  }
  if (sortKey.startsWith('nextMaturityDate')) {
    const value = compactCustomerDate(row?.nextMaturityDate)
    return value ? `다음 만기 ${value}` : ''
  }
  if (sortKey.startsWith('createdAt')) {
    const value = compactCustomerDate(row?.createdAtDisplay || row?.createdAt)
    return value ? `등록 ${value}` : ''
  }
  const value = compactCustomerDate(row?.lastTouchAtDisplay || row?.lastTouchAt || row?.updatedAtDisplay || row?.updatedAt)
  return value ? `마지막 활동 ${value}` : ''
}

const CUSTOMER_SORT_OPTIONS = [
  { value: 'lastTouchAt,DESC', label: '마지막 활동순' },
  { value: 'lastContactAt,DESC', label: '최근 연락순' },
  { value: 'latestContractDate,DESC', label: '최근 계약순' },
  { value: 'nextMaturityDate,ASC', label: '만기 임박순' },
  { value: 'createdAt,DESC', label: '최근 등록순' },
  { value: 'name,ASC', label: '가나다순' },
]

function parseMemoTagsInput(value) {
  return String(value || '')
    .split(/[#,\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item, index, arr) => arr.findIndex((candidate) => candidate.toLowerCase() === item.toLowerCase()) === index)
    .slice(0, 12)
}

function memoTitleFromFirstLine(value) {
  const firstLine = String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean)
  return firstLine ? firstLine.replace(/^#+\s*/, '').slice(0, 255) : ''
}

function memoHashTagsFromText(value) {
  const tags = []
  const re = /(^|\s)#([^#\s,.;:!?()[\]{}]{1,50})/g
  let match
  while ((match = re.exec(String(value || ''))) !== null) {
    const tag = String(match[2] || '').trim()
    if (tag && !tags.some((item) => item.toLowerCase() === tag.toLowerCase())) tags.push(tag)
    if (tags.length >= 12) break
  }
  return tags
}
function memoAttachmentPayload(attachments) {
  return (Array.isArray(attachments) ? attachments : [])
    .filter((att) => att?.fileId)
    .map((att, idx) => ({ fileId: att.fileId, caption: att.caption || null, sortOrder: idx }))
}

export default function CustomerManagementPage() {
  const navigate = useNavigate()
  const { hasAnyRole, user: authUser } = useAuth()
  const isOperator = hasAnyRole([Roles.OPERATOR])
  const [list, setList] = useState([])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [sortOption, setSortOption] = useState('lastTouchAt,DESC')
  const [quickFilter, setQuickFilter] = useState('all')
  const [ignoreFavoritePriority, setIgnoreFavoritePriority] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [customerFilters, setCustomerFilters] = useState(() => ({ ...EMPTY_CUSTOMER_LIST_FILTERS }))
  const [customerFilterOptions, setCustomerFilterOptions] = useState(() => INITIAL_CUSTOMER_FILTER_OPTIONS)
  const activeFilterCount = useMemo(() => activeCustomerListFilterCount(customerFilters), [customerFilters])
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createPhone, setCreatePhone] = useState('')
  const [createBirthDate, setCreateBirthDate] = useState('')
  const [createCustomerSegment, setCreateCustomerSegment] = useState('')
  const [createAffiliation, setCreateAffiliation] = useState('')
  const [createHonorific, setCreateHonorific] = useState('')
  const [createHomeAddress, setCreateHomeAddress] = useState('')
  const [createCompanyAddress, setCreateCompanyAddress] = useState('')
  const [createCustomerEmail, setCreateCustomerEmail] = useState('')
  const [createCustomerSource, setCreateCustomerSource] = useState('')
  const [createGender, setCreateGender] = useState('')
  const [createOccupation, setCreateOccupation] = useState('')
  const [createMonthlyIncome, setCreateMonthlyIncome] = useState('')
  const [createIncomeBasis, setCreateIncomeBasis] = useState('')
  const [createDualIncomeStatus, setCreateDualIncomeStatus] = useState('')
  const [createAnniversaries, setCreateAnniversaries] = useState([])
  const [createAnniversaryTitle, setCreateAnniversaryTitle] = useState('')
  const [createAnniversaryDate, setCreateAnniversaryDate] = useState('')
  const [createFamilyMembers, setCreateFamilyMembers] = useState([])
  const [createFamilyName, setCreateFamilyName] = useState('')
  const [createFamilyRelationship, setCreateFamilyRelationship] = useState('')
  const [createFamilyBirthDate, setCreateFamilyBirthDate] = useState('')
  const [createFamilyCohabiting, setCreateFamilyCohabiting] = useState('')
  const [createFamilyMemo, setCreateFamilyMemo] = useState('')
  const [createFamilyLinkedSearch, setCreateFamilyLinkedSearch] = useState('')
  const [createFamilyLinkCandidates, setCreateFamilyLinkCandidates] = useState([])
  const [createFamilyLinkedCustomerRefId, setCreateFamilyLinkedCustomerRefId] = useState('')
  const [createMemo, setCreateMemo] = useState('')
  const [createBusy, setCreateBusy] = useState(false)
  const [inlineCreateRow, setInlineCreateRow] = useState(null)
  const [inlineCreateBusy, setInlineCreateBusy] = useState(false)

  const [createBizWizardOpen, setCreateBizWizardOpen] = useState(false)
  const [createBizWizardStep, setCreateBizWizardStep] = useState('front')
  const [createBizDraftFrontId, setCreateBizDraftFrontId] = useState('')
  const [createBizDraftBackId, setCreateBizDraftBackId] = useState('')
  const [createBizWizardBusy, setCreateBizWizardBusy] = useState(false)
  const [createBizExtracting, setCreateBizExtracting] = useState(false)
  const [createPendingBizFrontId, setCreatePendingBizFrontId] = useState('')
  const [createPendingBizBackId, setCreatePendingBizBackId] = useState('')

  const createSegmentSelectOptions = useMemo(
    () => segmentOptionsForStored(createCustomerSegment),
    [createCustomerSegment],
  )
  const createSourceSelectOptions = useMemo(
    () => sourceOptionsForStored(createCustomerSource),
    [createCustomerSource],
  )

  const csvFileInputRef = useRef(null)
  const [csvExportBusy, setCsvExportBusy] = useState(false)
  const [csvImportBusy, setCsvImportBusy] = useState(false)

  const [memoModal, setMemoModal] = useState(null)
  const [memoEditorMode, setMemoEditorMode] = useState('simple')
  const [memoEditorTitle, setMemoEditorTitle] = useState('')
  const [memoDraft, setMemoDraft] = useState('')
  const [memoEditorContent, setMemoEditorContent] = useState(null)
  const [memoEditorId, setMemoEditorId] = useState(null)
  const [memoEditorVersion, setMemoEditorVersion] = useState(null)
  const [memoCategory, setMemoCategory] = useState('')
  const [memoTagsText, setMemoTagsText] = useState('')
  const [memoAttachments, setMemoAttachments] = useState([])
  const [memoTemplates] = useState([])
  const [memoPinned, setMemoPinned] = useState(false)
  const [memoSaving, setMemoSaving] = useState(false)
  const [memoDraftStatus, setMemoDraftStatus] = useState('')
  const memoDraftStorageKey = useMemo(
    () => (memoModal?.rich ? customerMemoDraftKey({
      user: authUser,
      surface: 'customer-list',
      customerId: memoModal?.id,
      memoId: memoEditorId || 'new',
    }) : ''),
    [authUser, memoEditorId, memoModal?.id, memoModal?.rich],
  )
  const [schedModal, setSchedModal] = useState(null)
  const [schedTitle, setSchedTitle] = useState('')
  const [schedDesc, setSchedDesc] = useState('')
  const [schedStart, setSchedStart] = useState('')
  const [schedEnd, setSchedEnd] = useState('')
  const [schedBusy, setSchedBusy] = useState(false)

  const [contactModal, setContactModal] = useState(null)
  const [contactAt, setContactAt] = useState('')
  const [contactContent, setContactContent] = useState('')
  const [contactBusy, setContactBusy] = useState(false)
  const [inlineSavingKey, setInlineSavingKey] = useState('')
  const [taskSelectSeq, setTaskSelectSeq] = useState({})

  const bumpTaskSelectReset = useCallback((customerId) => {
    if (customerId == null) return
    setTaskSelectSeq((prev) => ({ ...prev, [customerId]: (prev[customerId] || 0) + 1 }))
  }, [])

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchQuery(searchInput.trim())
    }, 300)
    return () => clearTimeout(handle)
  }, [searchInput])

  useEffect(() => {
    let cancelled = false
    api.get('/customer-management/customers/filter-options', { __silent: true })
      .then((res) => {
        if (cancelled) return
        const data = unwrap(res) || {}
        setCustomerFilterOptions({
          regions: mergeCustomerFilterOptions(data.regions),
          ageBands: mergeCustomerFilterOptions(data.ageBands, DEFAULT_CUSTOMER_AGE_BAND_OPTIONS),
          sources: mergeCustomerFilterOptions(data.sources, INITIAL_CUSTOMER_FILTER_OPTIONS.sources),
          segments: mergeCustomerFilterOptions(data.segments, INITIAL_CUSTOMER_FILTER_OPTIONS.segments),
        })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const loadList = useCallback(async (p = 0) => {
    setLoading(true)
    setErr('')
    try {
      const res = await api.get('/customer-management/customers', {
        params: {
          page: p,
          size: 20,
          sort: sortOption,
          ...(searchQuery ? { q: searchQuery } : {}),
          ...(quickFilter === 'favorites' ? { favoriteOnly: true } : {}),
          ...(ignoreFavoritePriority ? { ignoreFavoritePriority: true } : {}),
          ...customerListFilterParams(customerFilters),
        },
      })
      const data = unwrap(res) || {}
      setList(Array.isArray(data.items) ? data.items : [])
      setPage(typeof data.page === 'number' ? data.page : p)
      setPageSize(typeof data.size === 'number' && data.size > 0 ? data.size : 20)
      setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0)
      setTotalElements(typeof data.totalElements === 'number' ? data.totalElements : 0)
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || '목록을 불러오지 못했습니다.')
      setList([])
      setTotalElements(0)
    } finally {
      setLoading(false)
    }
  }, [customerFilters, ignoreFavoritePriority, quickFilter, searchQuery, sortOption])

  useEffect(() => {
    loadList(0)
  }, [loadList])
  const toggleCustomerFavorite = useCallback(async (row) => {
    const id = toCustomerId(row?.id)
    if (!id) return
    const nextFavorite = !customerIsFavorite(row)
    const previousList = list
    setList((prev) => prev
      .map((item) => (toCustomerId(item?.id) === id ? { ...item, isFavorite: nextFavorite } : item))
      .filter((item) => quickFilter !== 'favorites' || customerIsFavorite(item)))
    try {
      const res = await api.put(`/customer-management/customers/${id}/favorite`, { favorite: nextFavorite }, { __silent: true })
      const data = unwrap(res) || {}
      const serverFavorite = data.isFavorite === true
      setList((prev) => prev
        .map((item) => (toCustomerId(item?.id) === id ? { ...item, isFavorite: serverFavorite } : item))
        .filter((item) => quickFilter !== 'favorites' || customerIsFavorite(item)))
    } catch (e) {
      setList(previousList)
      setErr(getApiErrorMessage(e, '즐겨찾기 저장에 실패했습니다.'))
    }
  }, [list, quickFilter])

  const visibleList = list

  const favoriteCount = quickFilter === 'favorites'
    ? totalElements
    : list.filter(customerIsFavorite).length
  const hasActiveListConditions = Boolean(searchQuery) || quickFilter === 'favorites' || activeFilterCount > 0
  const resetListConditions = useCallback(() => {
    setSearchInput('')
    setSearchQuery('')
    setQuickFilter('all')
    setCustomerFilters({ ...EMPTY_CUSTOMER_LIST_FILTERS })
  }, [])
  useEffect(() => {
    if (createOpen) return
    setCreateBizWizardOpen(false)
    setCreateBizWizardStep('front')
    setCreateBizDraftFrontId('')
    setCreateBizDraftBackId('')
    setCreateBizExtracting(false)
    setCreatePendingBizFrontId('')
    setCreatePendingBizBackId('')
  }, [createOpen])

  const openCreateBizWizard = () => {
    setCreateBizWizardOpen(true)
    setCreateBizWizardStep('front')
    setCreateBizDraftFrontId('')
    setCreateBizDraftBackId('')
    setCreateBizExtracting(false)
    setErr('')
  }

  const uploadCreateBizCardFile = async (file) => {
    const form = new FormData()
    form.append('file', file)
    const up = await api.post('/files', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      __silent: true,
    })
    const payload = up?.data?.data ?? up?.data ?? {}
    const fid = payload.fileId || payload.id
    if (!fid) throw new Error('no_file_id')
    return fid
  }

  const runCreateBizCardExtractAndFill = async () => {
    if (!createBizDraftFrontId) {
      try { window?.toast?.show?.('명함 앞면 이미지를 업로드해 주세요.', 'error') } catch { }
      return
    }
    setCreateBizExtracting(true)
    setCreateBizWizardBusy(true)
    setErr('')
    try {
      const res = await api.post(
        '/customer-management/customers/extract-business-card-preview',
        {
          frontFileId: createBizDraftFrontId,
          backFileId: createBizDraftBackId || null,
        },
        { __silent: true },
      )
      const data = await unwrapAsyncResult(res)
      const patch = patchCreateFormFromExtracted(data)
      if (patch.customerName) setCreateName(patch.customerName)
      if (patch.customerPhoneDigits) setCreatePhone(formatKoreanPhoneInput(patch.customerPhoneDigits))
      if (patch.birthDateDigits) setCreateBirthDate(formatBirthYmdInput(patch.birthDateDigits))
      if (patch.customerSegment != null) setCreateCustomerSegment(patch.customerSegment)
      if (patch.affiliation != null) setCreateAffiliation(patch.affiliation)
      if (patch.honorific != null) setCreateHonorific(patch.honorific)
      if (patch.homeAddress != null) setCreateHomeAddress(patch.homeAddress)
      if (patch.companyAddress != null) setCreateCompanyAddress(patch.companyAddress)
      if (patch.customerEmail != null) setCreateCustomerEmail(patch.customerEmail)
      if (patch.customerSource != null) setCreateCustomerSource(patch.customerSource)
      setCreatePendingBizFrontId(createBizDraftFrontId)
      setCreatePendingBizBackId(createBizDraftBackId || '')
      setCreateBizWizardOpen(false)
      setCreateBizWizardStep('front')
      setCreateBizDraftFrontId('')
      setCreateBizDraftBackId('')
      try {
        window?.toast?.show?.('명함에서 정보를 읽어 폼에 반영했습니다. 확인 후 등록해 주세요.', 'success')
      } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '명함 인식에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCreateBizExtracting(false)
      setCreateBizWizardBusy(false)
    }
  }

  const resetMemoEditor = useCallback(() => {
    setMemoEditorTitle('')
    setMemoDraft('')
    setMemoEditorContent(null)
    setMemoEditorId(null)
    setMemoEditorVersion(null)
    setMemoCategory('')
    setMemoTagsText('')
    setMemoAttachments([])
    setMemoPinned(false)
    setMemoDraftStatus('')
  }, [])


  useEffect(() => {
    if (!memoDraftStorageKey) return
    if (memoEditorId || memoEditorContent || memoDraftStatus === '저장됨') return
    if (memoEditorTitle || memoDraft || memoCategory || memoTagsText || memoAttachments.length > 0) return
    try {
      const raw = window.sessionStorage?.getItem(memoDraftStorageKey)
      if (!raw) return
      const draft = JSON.parse(raw)
      setMemoEditorTitle(draft.title || '')
      setMemoDraft(draft.plainText || '')
      setMemoEditorContent(draft.content || null)
      setMemoCategory(draft.category || '')
      setMemoTagsText(draft.tagsText || '')
      setMemoAttachments(Array.isArray(draft.attachments) ? draft.attachments : [])
      setMemoPinned(draft.pinned === true)
      setMemoDraftStatus('임시저장 복원됨')
    } catch { }
  }, [memoAttachments.length, memoCategory, memoDraft, memoDraftStorageKey, memoEditorContent, memoEditorId, memoDraftStatus, memoEditorTitle, memoTagsText])

  useEffect(() => {
    if (!memoDraftStorageKey) return
    if (memoDraftStatus === '저장됨') return
    const hasDraft = Boolean(memoEditorTitle.trim() || memoDraft.trim() || memoCategory.trim() || memoTagsText.trim() || memoAttachments.length > 0)
    try {
      if (!hasDraft) {
        window.sessionStorage?.removeItem(memoDraftStorageKey)
        setMemoDraftStatus('')
        return
      }
      window.sessionStorage?.setItem(memoDraftStorageKey, JSON.stringify({
        title: memoEditorTitle,
        plainText: memoDraft,
        content: memoEditorContent,
        category: memoCategory,
        tagsText: memoTagsText,
        attachments: memoAttachments,
        pinned: memoPinned,
      }))
      if (!memoSaving) setMemoDraftStatus('임시저장됨')
    } catch { }
  }, [memoAttachments, memoCategory, memoDraft, memoDraftStatus, memoDraftStorageKey, memoEditorContent, memoEditorTitle, memoPinned, memoSaving, memoTagsText])

  useEffect(() => {
    const dirty = memoModal?.rich && memoDraftStatus && memoDraftStatus !== '저장됨'
    if (!dirty) return undefined
    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [memoDraftStatus, memoModal?.rich])
  const applyMemoTemplate = (template) => {
    if (!template) return
    const applied = applyCustomerMemoTemplateVariables(template, buildCustomerMemoTemplateVariables({ customer: memoModal?.customer, user: authUser }))
    const mode = chooseCustomerMemoTemplateApplyMode(hasCustomerMemoDraftContent({
      title: memoEditorTitle,
      plainText: memoDraft,
      category: memoCategory,
      tagsText: memoTagsText,
      attachments: memoAttachments,
    }))
    if (!mode) return
    const merged = mergeCustomerMemoTemplateDraft({
      title: memoEditorTitle,
      plainText: memoDraft,
      category: memoCategory,
      tagsText: memoTagsText,
      attachments: memoAttachments,
    }, applied, mode)
    setMemoEditorTitle(merged.title || '')
    setMemoDraft(merged.plainText || '')
    setMemoEditorContent(merged.content || null)
    setMemoCategory(merged.category || '')
    setMemoTagsText(merged.tagsText || '')
    setMemoAttachments(merged.attachments || [])
    if (applied.variableWarnings?.length) {
      try { window?.toast?.show?.(applied.variableWarnings[0], 'warning') } catch { }
    }
    recordCustomerMemoTemplateUsage(api, template, { customerRefId: memoModal?.id })
  }



  const openMemoModal = async (row) => {
    if (!row?.id) return
    resetMemoEditor()
    setMemoEditorMode('simple')
    setErr('')
    try {
      const res = await api.get(`/customer-management/customers/${row.id}/memos`, { __silent: true })
      const rows = Array.isArray(unwrap(res)) ? unwrap(res) : []
      const [memo] = [...rows].sort((a, b) => Number(b.pinned === true) - Number(a.pinned === true)
        || String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))
        || Number(b.id || 0) - Number(a.id || 0))
      if (memo) {
        setMemoEditorId(memo.id || null)
        setMemoEditorVersion(memo.version ?? null)
        setMemoEditorTitle(memo.title || '')
        setMemoDraft(memo.plainText || '')
        setMemoEditorContent(memo.content || null)
        setMemoCategory(memo.category || '')
        setMemoTagsText(Array.isArray(memo.tags) ? memo.tags.filter(Boolean).join(', ') : '')
        setMemoAttachments(Array.isArray(memo.attachments) ? memo.attachments : [])
        setMemoPinned(memo.pinned === true)
        setMemoDraftStatus('저장됨')
      } else if (row.memo != null && String(row.memo).trim()) {
        setMemoDraft(String(row.memo).trim())
        setMemoEditorContent(null)
        setMemoDraftStatus('')
      }
    } catch {
      if (row.memo != null && String(row.memo).trim()) {
        setMemoDraft(String(row.memo).trim())
        setMemoEditorContent(null)
        setMemoDraftStatus('')
      }
    }
    setMemoModal({ id: row.id, name: row.customerName || '(이름 없음)', rich: true, customer: row })
  }
  const memoRequestBody = () => {
    const simpleMode = memoEditorMode === 'simple'
    const tags = simpleMode ? memoHashTagsFromText(memoDraft) : parseMemoTagsInput(memoTagsText)
    return {
      title: simpleMode ? memoTitleFromFirstLine(memoDraft) || null : memoEditorTitle.trim() || null,
      plainText: memoDraft,
      pinned: memoPinned,
      version: memoEditorId ? memoEditorVersion : null,
      category: simpleMode ? null : memoCategory.trim() || null,
      tags,
      content: memoEditorContent || {
        schemaVersion: 1,
        blocks: memoDraft.trim() ? [{ type: 'paragraph', text: memoDraft }] : [],
      },
      attachments: memoAttachmentPayload(memoAttachments),
    }
  }
  const saveMemoFromList = async () => {
    if (!memoModal?.id) return
    setMemoSaving(true)
    setMemoDraftStatus('저장 중')
    setErr('')
    try {
      if (memoModal.rich) {
        if (!memoDraft.trim() && memoAttachments.length === 0) {
          try { window?.toast?.show?.('메모 내용을 입력해 주세요.', 'error') } catch { }
          setMemoDraftStatus('입력 필요')
          return
        }
        const body = memoRequestBody()
        const saveRes = memoEditorId
          ? await api.patch(`/customer-management/customers/${memoModal.id}/memos/${memoEditorId}`, body, { __silent: true })
          : await api.post(`/customer-management/customers/${memoModal.id}/memos`, body, { __silent: true })
        const savedMemo = unwrap(saveRes) || {}
        setMemoEditorId(savedMemo.id || memoEditorId || null)
        setMemoEditorVersion(savedMemo.version ?? memoEditorVersion ?? null)
        try { window.sessionStorage?.removeItem(memoDraftStorageKey) } catch { }
        setMemoDraftStatus('저장됨')
        try { window?.toast?.show?.('고객 메모가 저장되었습니다.', 'success') } catch { }
        setMemoModal(null)
        resetMemoEditor()
        await loadList(page)
        return
      }

      await api.patch(`/customer-management/customers/${memoModal.id}/memo`, { memo: memoDraft }, { __silent: true })
      try { window?.toast?.show?.('메모가 저장되었습니다.', 'success') } catch { }
      setMemoModal(null)
      await loadList(page)
    } catch (e) {
      const msg = getApiErrorMessage(e, '메모 저장에 실패했습니다.')
      setMemoDraftStatus('저장 실패')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setMemoSaving(false)
    }
  }
  const openScheduleModal = (row) => {
    if (!row?.id) return
    const { startLocal, endLocal } = defaultScheduleRangeLocal()
    setSchedModal({ id: row.id, name: row.customerName || '(이름 없음)' })
    setSchedTitle(`[고객] ${row.customerName || '고객'}`)
    setSchedDesc('')
    setSchedStart(startLocal)
    setSchedEnd(endLocal)
  }

  const submitSchedule = async () => {
    if (!schedModal?.id) return
    const title = (schedTitle || '').trim()
    if (!title) {
      try { window?.toast?.show?.('일정 제목을 입력해 주세요.', 'error') } catch { }
      return
    }
    let startAt
    let endAt
    try {
      startAt = new Date(schedStart).toISOString()
      endAt = new Date(schedEnd).toISOString()
    } catch {
      try { window?.toast?.show?.('시작·종료 시각을 확인해 주세요.', 'error') } catch { }
      return
    }
    if (!schedStart || !schedEnd || new Date(endAt) <= new Date(startAt)) {
      try { window?.toast?.show?.('종료 시각은 시작보다 이후여야 합니다.', 'error') } catch { }
      return
    }
    setSchedBusy(true)
    setErr('')
    try {
      await api.post(
        '/calendar/events/personal',
        {
          title,
          description: (schedDesc || '').trim() || null,
          startAt,
          endAt,
          allDay: false,
          timeZone: 'Asia/Seoul',
          useDefaultReminders: true,
          reminderOffsetsMinutes: null,
          rrule: null,
          customerRefId: schedModal.id,
        },
        { __silent: true },
      )
      try { window?.toast?.show?.('개인 일정으로 등록되었습니다. 고객에게 별도 알림은 전송되지 않습니다.', 'success') } catch { }
      setSchedModal(null)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '일정 등록에 실패했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setSchedBusy(false)
    }
  }

  const openContactModal = (row) => {
    if (!row?.id) return
    setContactModal({ id: row.id, name: row.customerName || '(이름 없음)' })
    setContactAt(defaultContactAtLocal())
    setContactContent('')
  }

  const runCustomerTaskAction = (row, action) => {
    if (!row?.id || !action) return
    const q = customerRefQuery(row.id)
    bumpTaskSelectReset(row.id)
    if (action === 'medical') {
      navigate(`/health-analysis?${q}`)
    } else if (action === 'hidden-insurance') {
      navigate(`/hidden-insurance-money?${q}`)
    } else if (action === 'claims') {
      navigate(`/support/claims?${q}`)
    } else if (action === 'fire') {
      navigate(`/fire/building-registry?${q}`)
    } else if (action === 'schedule') {
      openScheduleModal(row)
    } else if (action === 'contact') {
      openContactModal(row)
    } else if (action === 'memo') {
      openMemoModal(row)
    }
  }

  const submitContact = async () => {
    if (!contactModal?.id) return
    const content = (contactContent || '').trim()
    if (!content) {
      try { window?.toast?.show?.('연락 내용을 입력해 주세요.', 'error') } catch { }
      return
    }
    let start
    try {
      start = new Date(contactAt)
      if (!contactAt || Number.isNaN(start.getTime())) throw new Error('invalid_date')
    } catch {
      try { window?.toast?.show?.('연락 일시를 확인해 주세요.', 'error') } catch { }
      return
    }
    const end = new Date(start.getTime() + 30 * 60 * 1000)
    setContactBusy(true)
    setErr('')
    try {
      await api.post(
        '/calendar/events/personal',
        {
          title: `[연락] ${contactModal.name || '고객'}`,
          description: content,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          allDay: false,
          timeZone: 'Asia/Seoul',
          useDefaultReminders: false,
          reminderOffsetsMinutes: [],
          rrule: null,
          customerRefId: contactModal.id,
          personalCategory: 'OTHER',
        },
        { __silent: true },
      )
      try { window?.toast?.show?.('연락 이력이 등록되었습니다. 일정 메뉴에서도 확인할 수 있습니다.', 'success') } catch { }
      setContactModal(null)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '연락 이력 등록에 실패했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setContactBusy(false)
    }
  }

  const addInlineCustomerRow = () => {
    if (inlineCreateRow || inlineCreateBusy) return
    setInlineCreateRow({
      customerName: '',
      customerPhone: '',
      birthDate: '',
      customerSegment: '',
      customerSource: '',
    })
    setErr('')
  }

  const updateInlineCustomerRow = (field, value) => {
    setInlineCreateRow((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const cancelInlineCustomerRow = () => {
    if (inlineCreateBusy) return
    setInlineCreateRow(null)
  }

  const saveInlineCustomerRow = async () => {
    if (!inlineCreateRow || inlineCreateBusy) return
    const customerName = inlineCreateRow.customerName.trim()
    const customerPhone = inlineCreateRow.customerPhone.replace(/\D/g, '')
    if (!customerName) {
      try { window?.toast?.show?.('이름을 입력해 주세요.', 'error') } catch { }
      return
    }
    if (!(customerPhone.length === 10 || customerPhone.length === 11)) {
      try { window?.toast?.show?.('연락처는 숫자 10~11자리로 입력해 주세요.', 'error') } catch { }
      return
    }
    const birthNorm = normalizeBirthDateForApi(inlineCreateRow.birthDate)
    if (!birthNorm.ok) {
      try { window?.toast?.show?.(birthNorm.message, 'error') } catch { }
      return
    }
    setInlineCreateBusy(true)
    setErr('')
    try {
      const res = await api.post('/customer-management/customers', {
        customerName,
        customerPhone,
        birthDate: birthNorm.value,
        customerSegment: inlineCreateRow.customerSegment.trim(),
        customerSource: inlineCreateRow.customerSource.trim(),
      }, { __silent: true })
      const data = unwrap(res) || {}
      setInlineCreateRow(null)
      try {
        window?.toast?.show?.(
          data.created === false || data.existing === true
            ? '이미 등록된 고객입니다. 목록을 갱신했습니다.'
            : '고객을 등록했습니다.',
          'success',
        )
      } catch { }
      await loadList(0)
    } catch (e) {
      const message = getApiErrorMessage(e, '고객 등록에 실패했습니다.')
      setErr(message)
      try { window?.toast?.show?.(message, 'error') } catch { }
    } finally {
      setInlineCreateBusy(false)
    }
  }

  const saveListProfileField = async (row, field, value) => {
    if (!row?.id || !field) return
    let normalizedValue = value
    if (field === 'birthDate') {
      const norm = normalizeBirthDateForApi(value)
      if (!norm.ok) {
        try { window?.toast?.show?.(norm.message, 'error') } catch { }
        return
      }
      normalizedValue = norm.value
    }
    const before = row[field] == null ? '' : String(row[field])
    const after = normalizedValue == null ? '' : String(normalizedValue)
    if (before === after) return
    const key = `${row.id}:${field}`
    setInlineSavingKey(key)
    setErr('')
    try {
      const res = await api.patch(
        `/customer-management/customers/${row.id}`,
        { [field]: normalizedValue },
        { __silent: true },
      )
      const saved = unwrap(res) || {}
      setList((prev) => prev.map((item) => (item.id === row.id ? { ...item, ...saved } : item)))
      try { window?.toast?.show?.('고객 정보를 저장했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '고객 정보 저장에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setInlineSavingKey('')
    }
  }

  const openCreateAddressSearch = (target) => {
    if (createBusy) return
    try {
      openDaumPostcode((data) => {
        const address = formatDaumCustomerAddress(data)
        if (!address) return
        if (target === 'company') setCreateCompanyAddress(address)
        else setCreateHomeAddress(address)
      })
    } catch {
      try { window?.toast?.show?.('주소검색(카카오 우편번호) 스크립트를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.', 'error', 5000) } catch { }
    }
  }

  const addCreateAnniversary = () => {
    const title = createAnniversaryTitle.trim()
    const normalized = normalizeBirthDateForApi(createAnniversaryDate)
    if (!title || !String(createAnniversaryDate || '').trim()) {
      try { window?.toast?.show?.('기념일명과 날짜를 입력해 주세요.', 'error') } catch { }
      return
    }
    if (!normalized.ok) {
      try { window?.toast?.show?.('기념일 날짜를 YYYY-MM-DD 형식으로 입력해 주세요.', 'error') } catch { }
      return
    }
    setCreateAnniversaries((prev) => [...prev, { title, anniversaryDate: normalized.value }])
    setCreateAnniversaryTitle('')
    setCreateAnniversaryDate('')
  }

  const searchCreateFamilyLinkCandidates = async () => {
    const q = createFamilyLinkedSearch.trim()
    if (!q) {
      setCreateFamilyLinkCandidates([])
      return
    }
    try {
      const res = await api.get('/customer-management/customers', { params: { q, page: 0, size: 8 }, __silent: true })
      const data = unwrap(res)
      setCreateFamilyLinkCandidates(Array.isArray(data?.items) ? data.items : [])
    } catch {
      setCreateFamilyLinkCandidates([])
    }
  }

  const addCreateFamilyMember = () => {
    const name = createFamilyName.trim()
    const normalized = normalizeBirthDateForApi(createFamilyBirthDate)
    if (!name && !createFamilyLinkedCustomerRefId) {
      try { window?.toast?.show?.('가족 이름을 입력하거나 기존 고객을 연결해 주세요.', 'error') } catch { }
      return
    }
    if (String(createFamilyBirthDate || '').trim() && !normalized.ok) {
      try { window?.toast?.show?.('가족 생년월일을 YYYY-MM-DD 형식으로 입력해 주세요.', 'error') } catch { }
      return
    }
    setCreateFamilyMembers((prev) => [...prev, {
      name,
      relationship: createFamilyRelationship.trim(),
      birthDate: normalized.ok ? normalized.value : null,
      cohabiting: createFamilyCohabiting === '' ? null : createFamilyCohabiting === 'YES',
      memo: createFamilyMemo.trim(),
      linkedCustomerRefId: createFamilyLinkedCustomerRefId ? Number(createFamilyLinkedCustomerRefId) : null,
    }])
    setCreateFamilyName('')
    setCreateFamilyRelationship('')
    setCreateFamilyBirthDate('')
    setCreateFamilyCohabiting('')
    setCreateFamilyMemo('')
    setCreateFamilyLinkedSearch('')
    setCreateFamilyLinkCandidates([])
    setCreateFamilyLinkedCustomerRefId('')
  }
  const submitCreate = async () => {
    const name = (createName || '').trim()
    const phone = (createPhone || '').replace(/\D/g, '')
    if (!name) {
      try { window?.toast?.show?.('이름을 입력해 주세요.', 'error') } catch { }
      return
    }
    if (!(phone.length === 10 || phone.length === 11)) {
      try { window?.toast?.show?.('연락처는 숫자 10~11자리로 입력해 주세요.', 'error') } catch { }
      return
    }
    const birthNorm = normalizeBirthDateForApi(createBirthDate)
    if (!birthNorm.ok) {
      try { window?.toast?.show?.(birthNorm.message, 'error') } catch { }
      return
    }
    setCreateBusy(true)
    setErr('')
    try {
      const res = await api.post('/customer-management/customers', {
        customerName: name,
        customerPhone: phone,
        birthDate: birthNorm.value,
        gender: (createGender || '').trim(),
        memo: (createMemo || '').trim() || null,
        customerSegment: (createCustomerSegment || '').trim(),
        affiliation: (createAffiliation || '').trim(),
        honorific: (createHonorific || '').trim(),
        homeAddress: (createHomeAddress || '').trim(),
        companyAddress: (createCompanyAddress || '').trim(),
        customerEmail: (createCustomerEmail || '').trim() || null,
        customerSource: (createCustomerSource || '').trim(),
        occupation: (createOccupation || '').trim(),
        monthlyIncome: incomeManToWon(createMonthlyIncome),
        incomeBasis: (createIncomeBasis || '').trim(),
        dualIncomeStatus: (createDualIncomeStatus || '').trim(),
        anniversaries: createAnniversaries,
        familyMembers: createFamilyMembers,
      }, { __silent: true })
      const data = unwrap(res)
      const newId = data?.id
      if (newId != null && (createPendingBizFrontId || createPendingBizBackId)) {
        try {
          if (createPendingBizFrontId) {
            await api.post(
              `/customer-management/customers/${newId}/business-cards`,
              { fileId: createPendingBizFrontId, side: 'FRONT' },
              { __silent: true },
            )
          }
          if (createPendingBizBackId) {
            await api.post(
              `/customer-management/customers/${newId}/business-cards`,
              { fileId: createPendingBizBackId, side: 'BACK' },
              { __silent: true },
            )
          }
        } catch (eBiz) {
          const m = getApiErrorMessage(eBiz, '명함 이미지 연결에 실패했습니다.')
          try { window?.toast?.show?.(m, 'error', 4500) } catch { }
        }
      }
      const existingCustomer = data?.created === false || data?.existing === true
      try {
        window?.toast?.show?.(
          existingCustomer ? '이미 등록된 고객입니다. 기존 고객 상세로 이동합니다.' : '고객이 등록되었습니다.',
          'success',
        )
      } catch { }
      setCreateName('')
      setCreatePhone('')
      setCreateBirthDate('')
      setCreateCustomerSegment('')
      setCreateAffiliation('')
      setCreateHonorific('')
      setCreateHomeAddress('')
      setCreateCompanyAddress('')
      setCreateCustomerEmail('')
      setCreateCustomerSource('')
      setCreateGender('')
      setCreateOccupation('')
      setCreateMonthlyIncome('')
      setCreateIncomeBasis('')
      setCreateDualIncomeStatus('')
      setCreateAnniversaries([])
      setCreateAnniversaryTitle('')
      setCreateAnniversaryDate('')
      setCreateFamilyMembers([])
      setCreateFamilyName('')
      setCreateFamilyRelationship('')
      setCreateFamilyBirthDate('')
      setCreateFamilyCohabiting('')
      setCreateFamilyMemo('')
      setCreateFamilyLinkedSearch('')
      setCreateFamilyLinkCandidates([])
      setCreateFamilyLinkedCustomerRefId('')
      setCreateMemo('')
      setCreatePendingBizFrontId('')
      setCreatePendingBizBackId('')
      setCreateOpen(false)
      if (newId != null) {
        navigate(`/support/customers/${newId}`)
      } else {
        await loadList(page)
      }
    } catch (e) {
      const msg = getApiErrorMessage(e, '등록에 실패했습니다.')
      setErr(msg)
      if (e?.response?.status !== 409) {
        try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
      }
    } finally {
      setCreateBusy(false)
    }
  }

  const fetchAllCustomerRows = useCallback(async () => {
    const rows = []
    let pageIdx = 0
    const size = 100
    while (true) {
      const res = await api.get('/customer-management/customers', {
        params: {
          page: pageIdx,
          size,
          sort: sortOption,
          ...(searchQuery ? { q: searchQuery } : {}),
          ...(quickFilter === 'favorites' ? { favoriteOnly: true } : {}),
          ...(ignoreFavoritePriority ? { ignoreFavoritePriority: true } : {}),
          ...customerListFilterParams(customerFilters),
        },
      })
      const data = unwrap(res) || {}
      const items = Array.isArray(data.items) ? data.items : []
      rows.push(...items)
      const totalPagesValue = Number(data.totalPages || 0)
      if (!items.length || pageIdx + 1 >= totalPagesValue) break
      pageIdx += 1
    }
    return rows
  }, [customerFilters, ignoreFavoritePriority, quickFilter, searchQuery, sortOption])

  const downloadCsvExport = async () => {
    setCsvExportBusy(true)
    setErr('')
    try {
      const rows = await fetchAllCustomerRows()
      const headerLine = stringifyCsvRow(CUSTOMER_SPREADSHEET_EXPORT_HEADERS)
      const lines = [headerLine]
      for (const row of rows) {
        const bd = row.birthDate
        const birthDisp =
          bd == null || bd === ''
            ? ''
            : String(bd).length >= 10
              ? String(bd).slice(0, 10)
              : String(bd)
        const cells = [
          row.customerName ?? '',
          excelCsvPhoneCell(row.customerPhone),
          row.customerSegment ?? '',
          birthDisp,
          row.region ?? '',
          row.affiliation ?? '',
          row.honorific ?? '',
          row.homeAddress ?? '',
          row.companyAddress ?? '',
          row.customerEmail ?? '',
          row.customerSource ?? '',
          row.memo ?? '',
        ]
        lines.push(stringifyCsvRow(cells))
      }
      const fname = `고객목록_${new Date().toISOString().slice(0, 10)}.csv`
      downloadUtf8BomFile(fname, lines.join('\r\n'))
      try {
        window?.toast?.show?.(`CSV를 저장했습니다. (${rows.length}명)`, 'success')
      } catch { }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'CSV 저장에 실패했습니다.'
      setErr(msg)
      try {
        window?.toast?.show?.(msg, 'error')
      } catch { }
    } finally {
      setCsvExportBusy(false)
    }
  }

  const importCustomerSpreadsheetRow = async (parsed) => {
    const digits = restoreLeadingZeroKoreanPhoneDigits(parsed.customerPhone)
    if (!(digits.length === 10 || digits.length === 11)) {
      throw new Error('핸드폰은 숫자 10~11자리로 입력해 주세요.')
    }

    const nameTrim = String(parsed.customerName || '').trim()
    if (!nameTrim) {
      throw new Error('이름을 입력해 주세요.')
    }

    const birthNorm = normalizeBirthDateForCsvImport(parsed.birthDateRaw)
    if (!birthNorm.ok) {
      throw new Error(birthNorm.message)
    }

    const homeAddress = String(parsed.homeAddress || '').trim()
    const memoTrim = String(parsed.memo || '').trim()
    const lookupResponse = await api.get('/customer-management/customers/lookup-by-phone', {
      params: { customerPhone: digits },
      __silent: true,
    })
    const lookup = unwrap(lookupResponse) || {}

    const profilePayload = {
      customerName: nameTrim,
      customerPhone: digits,
    }
    if (birthNorm.value) profilePayload.birthDate = birthNorm.value
    if (homeAddress) profilePayload.homeAddress = homeAddress

    if (lookup.registered && lookup.customerRefId != null) {
      await api.patch(
        `/customer-management/customers/${lookup.customerRefId}`,
        profilePayload,
        { __silent: true },
      )
      if (memoTrim) {
        await api.patch(
          `/customer-management/customers/${lookup.customerRefId}/memo`,
          { memo: memoTrim },
          { __silent: true },
        )
      }
      return 'updated'
    }

    await api.post(
      '/customer-management/customers',
      {
        customerName: nameTrim,
        customerPhone: digits,
        birthDate: birthNorm.value,
        homeAddress: homeAddress || null,
        memo: memoTrim || null,
      },
      { __silent: true },
    )
    return 'created'
  }

  const onCustomerSpreadsheetFileChange = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setCsvImportBusy(true)
    setErr('')
    let created = 0
    let updated = 0
    const rowErrors = []

    try {
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('엑셀 또는 CSV 파일은 10MB 이하만 업로드할 수 있습니다.')
      }

      const filename = String(file.name || '').toLowerCase()
      let parsedRows = []
      if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) {
        const form = new FormData()
        form.append('file', file)
        const response = await api.post('/customer-management/customers/import/parse-excel', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          __silent: true,
        })
        const preview = unwrap(response) || {}
        parsedRows = Array.isArray(preview.rows) ? preview.rows : []
      } else if (filename.endsWith('.csv')) {
        const text = await readCustomerImportCsvFileAsText(file)
        const parsedCsv = parseCustomerImportCsv(text)
        const validation = validateCustomerImportHeaders(parsedCsv.headers)
        if (!validation.ok) throw new Error(validation.message)
        if (parsedCsv.rows.length > 2000) {
          throw new Error('한 번에 최대 2,000명의 고객만 업로드할 수 있습니다.')
        }
        parsedRows = parsedCsv.rows.map((cells, index) => ({
          ...mapCustomerImportRow(parsedCsv.headers, cells),
          rowNumber: index + 2,
        }))
      } else {
        throw new Error('엑셀(.xlsx, .xls) 또는 CSV(.csv) 파일만 업로드할 수 있습니다.')
      }

      if (parsedRows.length === 0) {
        throw new Error('가져올 고객 데이터가 없습니다. 첫 번째 행 아래에 고객 정보를 입력해 주세요.')
      }

      for (let index = 0; index < parsedRows.length; index += 1) {
        const parsed = parsedRows[index]
        const rowNumber = Number(parsed.rowNumber) || index + 2
        try {
          const outcome = await importCustomerSpreadsheetRow(parsed)
          if (outcome === 'created') created += 1
          if (outcome === 'updated') updated += 1
        } catch (rowError) {
          rowErrors.push(`${rowNumber}행: ${getApiErrorMessage(rowError, '처리 실패')}`)
        }
      }

      const parts = []
      if (created) parts.push(`신규 ${created}명`)
      if (updated) parts.push(`갱신 ${updated}명`)
      if (rowErrors.length === 0) {
        try {
          window?.toast?.show?.(
            parts.length ? `가져오기 완료 (${parts.join(', ')})` : '처리할 유효한 데이터가 없었습니다.',
            'success',
          )
        } catch { }
      } else {
        const anySucceeded = created > 0 || updated > 0
        try {
          window?.toast?.show?.(
            anySucceeded
              ? `가져오기 ${parts.join(', ')} · 실패 ${rowErrors.length}건`
              : `가져오기 실패 ${rowErrors.length}건`,
            'error',
          )
        } catch { }
        setErr(
          (anySucceeded
            ? '일부 행만 실패했습니다.\n\n'
            : '아래 행은 처리하지 못했습니다. 핸드폰·이름·생년월일을 확인해 주세요.\n\n')
            + rowErrors.join('\n'),
        )
      }
      await loadList(page)
    } catch (error) {
      const message = getApiErrorMessage(error, '고객 엑셀 가져오기에 실패했습니다.')
      setErr(message)
      try {
        window?.toast?.show?.(message, 'error')
      } catch { }
    } finally {
      setCsvImportBusy(false)
    }
  }

  return (
    <PageFrame>
      <div className="mx-auto w-full max-w-[1600px] space-y-5">
        <PageHeader
          title="고객관리"
          titleAccessory={<MenuGuideButton slug="support-customers" title="고객관리 가이드" />}
          actions={
            <Button
              type="button"
              variant="sgez"
              size="sm"
              className={CRM_HEADER_BTN_SIZE}
              onClick={() => setCreateOpen(true)}
            >
              고객 등록
            </Button>
          }
        />

      {err ? (
        <div className="rounded-2xl border border-rose-200 bg-white">
          <ErrorState
            description={err}
            onAction={() => loadList(page)}
            className="py-6"
          />
        </div>
      ) : null}

        <WorkSurface className="rounded-2xl">
          <ToolbarSurface className="space-y-3 px-3 py-3 sm:px-4">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/support/customers/work-queue"
                className={`${CRM_HEADER_BTN} border-teal-200 bg-teal-50 text-teal-900 hover:bg-teal-100`}
              >
                고객업무 허브
              </Link>
              <Link
                to="/support/customer-memos"
                className={`${CRM_HEADER_BTN} border-sgez-border-strong bg-sgez-surface text-slate-700 hover:bg-sgez-surface-subtle`}
              >
                고객메모
              </Link>
              <Link
                to="/support/maturity-ledger"
                className={`${CRM_HEADER_BTN} border-sgez-border-strong bg-sgez-surface text-slate-700 hover:bg-sgez-surface-subtle`}
              >
                보험만기대장
              </Link>
              {isOperator && (
                <Link
                  to="/support/customer-registration"
                  title="범위 선택·보험사·동의 등 단계별 진행 (운영자)"
                  className={`${CRM_HEADER_BTN} border-sky-200 bg-sky-50 text-sky-900 hover:bg-sky-100`}
                >
                  고객등록
                </Link>
              )}
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="sgezOutline"
                  size="sm"
                  className={`${CRM_HEADER_BTN_SIZE} hidden xl:inline-flex`}
                  disabled={Boolean(inlineCreateRow) || inlineCreateBusy}
                  onClick={addInlineCustomerRow}
                >
                  새 행 추가
                </Button>
                <Button
                  type="button"
                  variant="sgezOutline"
                  size="sm"
                  className={CRM_HEADER_BTN_SIZE}
                  disabled={csvExportBusy || csvImportBusy}
                  onClick={() => downloadCsvExport()}
                >
                  {csvExportBusy ? 'CSV 저장 중…' : 'CSV 내려받기'}
                </Button>
                <Button
                  type="button"
                  variant="sgezOutline"
                  size="sm"
                  className={CRM_HEADER_BTN_SIZE}
                  disabled={csvExportBusy || csvImportBusy}
                  onClick={() => csvFileInputRef.current?.click()}
                >
                  {csvImportBusy ? '엑셀 가져오는 중…' : '엑셀 올리기'}
                </Button>
                <input
                  ref={csvFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  className="hidden"
                  aria-hidden
                  onChange={onCustomerSpreadsheetFileChange}
                />
                <Button
                  type="button"
                  variant="sgezOutline"
                  size="sm"
                  className={CRM_HEADER_BTN_SIZE}
                  onClick={() => loadList(page)}
                >
                  새로고침
                </Button>
              </div>
            </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-x-3 sm:gap-y-2">
          <div className="text-sm font-semibold text-slate-800">
            고객 목록
            <span className="ml-2 text-xs font-medium text-slate-500">전체 {totalElements.toLocaleString()}명</span>
          </div>
          <label className="flex w-full min-w-0 items-center gap-2 text-xs font-semibold text-slate-600 sm:w-auto sm:flex-1 sm:min-w-[240px]">
            <span className="shrink-0">검색</span>
            <input
              type="search"
              value={searchInput}
              aria-label="고객 검색"
              placeholder="이름·연락처·이메일"
              className="h-8 min-w-0 flex-1 rounded border border-sgez-border-strong bg-sgez-surface px-2 text-xs font-medium text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchQuery(searchInput.trim())
                }
              }}
            />
            {searchInput ? (
              <button
                type="button"
                className="h-8 rounded border border-sgez-border-strong bg-sgez-surface px-2 text-xs font-bold text-slate-500 hover:bg-sgez-surface-subtle"
                onClick={() => {
                  setSearchInput('')
                  setSearchQuery('')
                }}
              >
                초기화
              </button>
            ) : null}
          </label>
          <label className="flex min-w-0 items-center gap-2 text-xs font-semibold text-slate-600 sm:w-auto">
            <span className="shrink-0">정렬</span>
            <select
              value={sortOption}
              aria-label="고객 목록 정렬"
              className="h-8 min-w-0 flex-1 rounded border border-sgez-border-strong bg-sgez-surface px-2 text-xs font-medium text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky sm:flex-none"
              onChange={(e) => {
                const next = e.target.value
                setSortOption(next)
              }}
            >
              {CUSTOMER_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <Button
            type="button"
            variant="sgezOutline"
            size="sm"
            className={`relative h-8 gap-1.5 rounded-md px-2.5 text-xs font-bold ${activeFilterCount > 0 ? 'border-brand-navy bg-blue-50 text-brand-navy' : ''}`}
            title="고객 필터"
            onClick={() => setFilterOpen(true)}
          >
            <ListFilter size={15} aria-hidden="true" />
            <span>필터</span>
            {activeFilterCount > 0 ? (
              <span className="inline-flex min-w-4 items-center justify-center rounded-full bg-brand-navy px-1 text-[10px] leading-4 text-white">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>
          <label className="inline-flex h-8 items-center gap-2 rounded-full border border-sgez-border-strong bg-sgez-surface px-3 text-xs font-bold text-slate-600">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-slate-300 text-brand-navy focus:ring-brand-sky"
              checked={!ignoreFavoritePriority}
              onChange={(e) => setIgnoreFavoritePriority(!e.target.checked)}
            />
            즐겨찾기 우선
          </label>
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label="고객 목록 필터">
            {CUSTOMER_QUICK_FILTERS.map((filter) => {
              const selected = quickFilter === filter.value
              const count = filter.value === 'favorites' ? favoriteCount : totalElements
              return (
                <button
                  key={filter.value}
                  type="button"
                  className={`h-8 rounded-full border px-3 text-xs font-bold transition ${selected ? 'border-brand-navy bg-brand-navy text-white' : 'border-sgez-border bg-sgez-surface text-slate-600 hover:bg-sgez-surface-subtle'}`}
                  aria-pressed={selected}
                  onClick={() => setQuickFilter(filter.value)}
                >
                  {filter.label} <span className="font-semibold opacity-80">{Number(count || 0).toLocaleString()}</span>
                </button>
              )
            })}
          </div>
          </div>
        </ToolbarSurface>
        <div className="md:overflow-x-auto md:overflow-y-auto">
          {loading ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">불러오는 중…</div>
          ) : visibleList.length === 0 && !inlineCreateRow ? (
            <EmptyState
              title={hasActiveListConditions ? '조건에 맞는 고객이 없습니다' : '등록된 고객이 없습니다'}
              description={hasActiveListConditions ? '검색어나 필터 조건을 다시 확인해 주세요.' : '고객 등록으로 직접 추가하거나 고객 정보 연결을 통해 관리할 수 있습니다.'}
              action={hasActiveListConditions ? (
                <Button
                  type="button"
                  variant="sgezOutline"
                  size="sm"
                  onClick={resetListConditions}
                >
                  조건 초기화
                </Button>
              ) : quickFilter !== 'favorites' && list.length === 0 ? (
                <Button type="button" variant="sgez" size="sm" onClick={() => setCreateOpen(true)}>
                  고객 등록
                </Button>
              ) : (
                <Button type="button" variant="sgezOutline" size="sm" onClick={() => setQuickFilter('all')}>
                  전체 보기
                </Button>
              )}
            />
          ) : (
            <div className="min-w-0">
              <div className="space-y-3 bg-sgez-surface-subtle p-3 xl:hidden">
                {inlineCreateRow ? (
                  <article className="rounded-lg border border-sgez-primary/30 bg-white p-4 shadow-sm">
                    <strong className="mb-3 block text-sm font-bold text-sgez-ink">새 고객</strong>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        className="col-span-2 h-10 rounded-md border border-sgez-border px-3 text-sm outline-none focus:border-sgez-primary"
                        placeholder="이름 *"
                        value={inlineCreateRow.customerName}
                        onChange={(event) => updateInlineCustomerRow('customerName', event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') saveInlineCustomerRow()
                        }}
                      />
                      <input
                        className="h-10 rounded-md border border-sgez-border px-3 text-sm outline-none focus:border-sgez-primary"
                        placeholder="연락처 *"
                        inputMode="tel"
                        value={inlineCreateRow.customerPhone}
                        onChange={(event) => updateInlineCustomerRow('customerPhone', formatKoreanPhoneInput(event.target.value))}
                      />
                      <input
                        className="h-10 rounded-md border border-sgez-border px-3 text-sm outline-none focus:border-sgez-primary"
                        placeholder="생년월일"
                        inputMode="numeric"
                        value={inlineCreateRow.birthDate}
                        onChange={(event) => updateInlineCustomerRow('birthDate', formatBirthYmdInput(event.target.value))}
                      />
                      <select
                        className="h-10 rounded-md border border-sgez-border bg-white px-3 text-sm outline-none focus:border-sgez-primary"
                        value={inlineCreateRow.customerSegment}
                        onChange={(event) => updateInlineCustomerRow('customerSegment', event.target.value)}
                      >
                        <option value="">고객 구분</option>
                        {segmentOptionsForStored(inlineCreateRow.customerSegment).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <select
                        className="h-10 rounded-md border border-sgez-border bg-white px-3 text-sm outline-none focus:border-sgez-primary"
                        value={inlineCreateRow.customerSource}
                        onChange={(event) => updateInlineCustomerRow('customerSource', event.target.value)}
                      >
                        <option value="">고객 출처</option>
                        {sourceOptionsForStored(inlineCreateRow.customerSource).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        type="button"
                        className="btn-secondary h-9 px-3 text-sm"
                        onClick={cancelInlineCustomerRow}
                        disabled={inlineCreateBusy}
                      >
                        취소
                      </button>
                      <button
                        type="button"
                        className="btn-primary h-9 px-3 text-sm"
                        onClick={saveInlineCustomerRow}
                        disabled={inlineCreateBusy}
                      >
                        {inlineCreateBusy ? '저장 중…' : '저장'}
                      </button>
                    </div>
                  </article>
                ) : null}
                {visibleList.map((row) => {
                  const memoRaw = row.memo != null && String(row.memo).trim() !== '' ? String(row.memo).trim() : ''
                  const phone = row.customerPhone ? formatKoreanPhoneInput(row.customerPhone) : ''
                  const birth = formatYmd(row.birthDate)
                  const segmentLabel = optionDisplayLabel(segmentOptionsForStored(row.customerSegment), row.customerSegment)
                  const sourceLabel = optionDisplayLabel(sourceOptionsForStored(row.customerSource), row.customerSource)
                  const headline = customerSortHeadline(row, sortOption)
                  const isFavorite = customerIsFavorite(row)
                  const isInactive = row.customerStatus === 'INACTIVE'
                  const meta = [phone || '연락처 없음', birth, row.region].filter(Boolean)
                  return (
                    <article
                      key={`mobile-customer-${row.id}`}
                      className={`rounded-xl border p-3 shadow-sm ring-1 ${isInactive ? 'border-slate-200 bg-slate-100 text-slate-500 ring-slate-200' : 'border-sgez-border bg-sgez-surface ring-sgez-border'}`}
                    >
                      <div className="flex min-w-0 items-start justify-between gap-3">
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => navigate(`/support/customers/${row.id}`)}
                        >
                          <div className="truncate text-sm font-bold text-slate-900">
                            {row.customerName || '(이름 없음)'}
                          </div>
                          {meta.length > 0 ? (
                            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] leading-5 text-slate-500">
                              {meta.map((item) => (
                                <span key={item} className="max-w-full truncate">{item}</span>
                              ))}
                            </div>
                          ) : null}
                        </button>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            id={`mobile-favorite-${row.id}`}
                            className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-base leading-none transition ${isFavorite ? 'border-amber-300 bg-amber-50 text-amber-500' : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50 hover:text-amber-500'}`}
                            title={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                            aria-label={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                            aria-pressed={isFavorite}
                            onClick={() => toggleCustomerFavorite(row)}
                          >
                            <Star aria-hidden="true" className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} />
                          </button>
                          <Link
                            to={`/support/customers/${row.id}`}
                            className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-bold text-brand-navy shadow-sm hover:bg-slate-50"
                          >
                            상세
                          </Link>
                        </div>
                      </div>
                      {(segmentLabel || sourceLabel || headline) ? (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {segmentLabel ? (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-800">
                              {segmentLabel}
                            </span>
                          ) : null}
                          {sourceLabel ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                              {sourceLabel}
                            </span>
                          ) : null}
                          {headline ? (
                            <span className="min-w-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                              {headline}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      {memoRaw ? (
                        <div className="mt-2 line-clamp-2 rounded border border-slate-100 bg-slate-50 px-2.5 py-2 text-xs leading-5 text-slate-600">
                          {memoRaw}
                        </div>
                      ) : null}
                      <div className="mt-3 grid grid-cols-3 gap-1.5">
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
                          disabled={isInactive}
                          onClick={() => openContactModal(row)}
                        >
                          연락 기록
                        </button>
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
                          disabled={isInactive}
                          onClick={() => openScheduleModal(row)}
                        >
                          일정
                        </button>
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
                          disabled={isInactive}
                          onClick={() => openMemoModal(row)}
                        >
                          메모
                        </button>
                      </div>
                      <select
                        key={`mobile-list-task-${row.id}-${taskSelectSeq[row.id] || 0}`}
                        defaultValue=""
                        disabled={isInactive}
                        aria-label={`${row.customerName || '고객'} 업무 선택`}
                        className="mt-2 h-9 w-full rounded border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                        onChange={(e) => runCustomerTaskAction(row, e.target.value)}
                      >
                        <option value="">업무 선택</option>
                        <option value="medical">진료정보분석</option>
                        <option value="hidden-insurance">숨은보험금</option>
                        <option value="claims">보험금청구</option>
                        <option value="fire">화재설계</option>
                      </select>
                    </article>
                  )
                })}
              </div>
              <div className="hidden min-w-0 xl:block">
              <table className="w-full max-w-full table-fixed border-collapse text-left text-sm">
                <colgroup>
                  <col className="w-[5%]" />
                  <col className="w-[15%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[20%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                </colgroup>
                <thead className="bg-sgez-surface-sunken">
                  <tr className="border-b border-sgez-border-strong text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="py-2 px-1 text-center leading-tight">no</th>
                    <th className="py-2 px-1.5 leading-tight">고객명</th>
                    <th className="py-2 px-1.5 leading-tight">전화</th>
                    <th className="py-2 px-1.5 leading-tight whitespace-normal">생년월일<br /><span className="font-normal normal-case text-[10px] text-slate-400">(상령일)</span></th>
                    <th className="py-2 px-1.5 leading-tight">구분</th>
                    <th className="py-2 px-1.5 leading-tight">출처</th>
                    <th className="py-2 px-1 text-center leading-tight">{'\uC5C5\uBB34\uBC14\uB85C\uAC00\uAE30'}</th>
                    <th className="py-2 px-1 text-center leading-tight">메모</th>
                    <th className="py-2 px-1 text-center leading-tight">상세</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sgez-border">
                  {inlineCreateRow ? (
                    <tr className="bg-sky-50/70 align-middle">
                      <td className="py-2 px-1 text-center text-[11px] font-semibold text-sky-700">새</td>
                      <td className="min-w-0 px-1.5 py-2">
                        <input
                          autoFocus
                          type="text"
                          value={inlineCreateRow.customerName}
                          placeholder="이름"
                          maxLength={100}
                          disabled={inlineCreateBusy}
                          className="w-full min-w-0 rounded border border-sky-300 bg-white px-1.5 py-1 text-xs text-slate-900 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-slate-100"
                          onChange={(e) => updateInlineCustomerRow('customerName', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              saveInlineCustomerRow()
                            }
                          }}
                        />
                      </td>
                      <td className="min-w-0 px-1.5 py-2">
                        <input
                          type="tel"
                          inputMode="numeric"
                          value={inlineCreateRow.customerPhone}
                          placeholder="연락처"
                          maxLength={13}
                          disabled={inlineCreateBusy}
                          className="w-full min-w-0 rounded border border-sky-300 bg-white px-1.5 py-1 text-[11px] tabular-nums text-slate-900 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-slate-100"
                          onChange={(e) => updateInlineCustomerRow('customerPhone', formatKoreanPhoneInput(e.target.value))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              saveInlineCustomerRow()
                            }
                          }}
                        />
                      </td>
                      <td className="min-w-0 px-1.5 py-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={inlineCreateRow.birthDate}
                          placeholder="YYYY-MM-DD"
                          maxLength={10}
                          disabled={inlineCreateBusy}
                          className="w-full min-w-0 rounded border border-sky-300 bg-white px-1 py-1 text-[11px] tabular-nums text-slate-900 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-slate-100"
                          onChange={(e) => updateInlineCustomerRow('birthDate', formatBirthYmdInput(e.target.value))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              saveInlineCustomerRow()
                            }
                          }}
                        />
                      </td>
                      <td className="min-w-0 px-1.5 py-2">
                        <select
                          value={inlineCreateRow.customerSegment}
                          disabled={inlineCreateBusy}
                          className="w-full min-w-0 rounded border border-sky-300 bg-white px-1 py-1 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-slate-100"
                          onChange={(e) => updateInlineCustomerRow('customerSegment', e.target.value)}
                        >
                          {segmentOptionsForStored(inlineCreateRow.customerSegment).map((opt) => (
                            <option key={opt.value || '__empty'} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="min-w-0 px-1.5 py-2">
                        <select
                          value={inlineCreateRow.customerSource}
                          disabled={inlineCreateBusy}
                          className="w-full min-w-0 rounded border border-sky-300 bg-white px-1 py-1 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-slate-100"
                          onChange={(e) => updateInlineCustomerRow('customerSource', e.target.value)}
                        >
                          {sourceOptionsForStored(inlineCreateRow.customerSource).map((opt) => (
                            <option key={opt.value || '__empty'} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1 py-2 text-center text-xs text-slate-400">-</td>
                      <td className="px-1 py-2 text-center text-xs text-slate-400">-</td>
                      <td className="px-1 py-2 text-center align-middle">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            className="inline-flex h-7 items-center justify-center rounded bg-brand-navy px-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                            disabled={inlineCreateBusy}
                            onClick={saveInlineCustomerRow}
                          >
                            {inlineCreateBusy ? '저장 중…' : '저장'}
                          </button>
                          <button
                            type="button"
                            className="inline-flex h-7 items-center justify-center rounded border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={inlineCreateBusy}
                            onClick={cancelInlineCustomerRow}
                          >
                            취소
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  {visibleList.map((row, idx) => {
                    const absoluteIndex = page * pageSize + idx
                    const fallbackTotal = page * pageSize + visibleList.length
                    const rowNo = Math.max((totalElements || fallbackTotal) - absoluteIndex, 1)
                    const phone = row.customerPhone ? formatKoreanPhoneInput(row.customerPhone) : ''
                    const birth = formatYmd(row.birthDate)
                    const insuranceAgeDate = formatYmd(row.insuranceAgeDate)
                    const q = customerRefQuery(row.id)
                    const isFavorite = customerIsFavorite(row)
                  const isInactive = row.customerStatus === 'INACTIVE'
                    return (
                      <tr key={row.id} className={`align-middle ${isInactive ? 'bg-slate-100/80 text-slate-500' : 'hover:bg-sgez-surface-subtle/70'}`}>
                        <td className="py-2 px-1 text-center text-[11px] tabular-nums text-slate-500">{rowNo}</td>
                        <td className="min-w-0 px-1.5 py-2">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <button
                              type="button"
                              id={`desktop-favorite-${row.id}`}
                              className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-sm leading-none transition ${isFavorite ? 'border-amber-300 bg-amber-50 text-amber-500' : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50 hover:text-amber-500'}`}
                              title={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                              aria-label={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                              aria-pressed={isFavorite}
                              onClick={() => toggleCustomerFavorite(row)}
                            >
                              <Star aria-hidden="true" className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} />
                            </button>
                            <span className="truncate text-xs font-medium text-slate-900" title={row.customerName || '(이름 없음)'}>
                              {row.customerName || '(이름 없음)'}
                            </span>
                          </div>
                        </td>
                        <td className="min-w-0 px-1.5 py-2 text-[11px] text-slate-700 tabular-nums break-all">
                          {phone || '—'}
                        </td>
                        <td className="min-w-0 px-1.5 py-2 text-[11px] text-slate-700 tabular-nums">
                          <input
                            type="text"
                            inputMode="numeric"
                            defaultValue={birth}
                            placeholder="YYYY-MM-DD"
                            disabled={isInactive || inlineSavingKey === `${row.id}:birthDate`}
                            className="w-full min-w-0 rounded border border-sgez-border-strong bg-sgez-surface px-1 py-0.5 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-sgez-surface-subtle"
                            onBlur={(e) => saveListProfileField(row, 'birthDate', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur()
                            }}
                          />
                          <div className="mt-0.5 truncate text-[10px] text-slate-500" title={insuranceAgeDate ? `상령일 ${insuranceAgeDate}` : undefined}>
                            상령일 {insuranceAgeDate || '—'}
                          </div>
                        </td>
                        <td className="min-w-0 px-1.5 py-2 text-xs text-slate-700">
                          <select
                            value={row.customerSegment || ''}
                            disabled={isInactive || inlineSavingKey === `${row.id}:customerSegment`}
                            className="w-full min-w-0 rounded border border-sgez-border-strong bg-sgez-surface px-1 py-0.5 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-sgez-surface-subtle"
                            onChange={(e) => saveListProfileField(row, 'customerSegment', e.target.value)}
                          >
                            {segmentOptionsForStored(row.customerSegment).map((opt) => (
                              <option key={opt.value || '__empty'} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="min-w-0 px-1.5 py-2 text-xs text-slate-700">
                          <select
                            value={row.customerSource || ''}
                            disabled={isInactive || inlineSavingKey === `${row.id}:customerSource`}
                            className="w-full min-w-0 rounded border border-sgez-border-strong bg-sgez-surface px-1 py-0.5 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-sgez-surface-subtle"
                            onChange={(e) => saveListProfileField(row, 'customerSource', e.target.value)}
                          >
                            {sourceOptionsForStored(row.customerSource).map((opt) => (
                              <option key={opt.value || '__empty'} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="min-w-0 px-1 py-2">
                          <select
                            key={`list-task-${row.id}-${taskSelectSeq[row.id] || 0}`}
                            defaultValue=""
                            disabled={isInactive}
                            aria-label="업무"
                            title="업무 선택"
                            className="w-full min-w-0 cursor-pointer rounded border border-sgez-border-strong bg-sgez-surface px-1 py-0.5 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
                            onChange={(e) => {
                              const action = e.target.value
                              if (!action) return
                              bumpTaskSelectReset(row.id)
                              if (action === 'medical') {
                                navigate(`/health-analysis?${q}`)
                              } else if (action === 'hidden-insurance') {
                                navigate(`/hidden-insurance-money?${q}`)
                              } else if (action === 'claims') {
                                navigate(`/support/claims?${q}`)
                              } else if (action === 'fire') {
                                navigate(`/fire/building-registry?${q}`)
                              } else if (action === 'schedule') {
                                openScheduleModal(row)
                              } else if (action === 'contact') {
                                openContactModal(row)
                              }
                            }}
                          >
                            <option value="">업무</option>
                            <option value="medical">진료</option>
                            <option value="hidden-insurance">숨은보험금</option>
                            <option value="claims">보험금청구</option>
                            <option value="fire">화재</option>
                            <option value="schedule">일정</option>
                            <option value="contact">연락</option>
                          </select>
                        </td>
                        <td className="px-1 py-2 text-center align-middle">
                          <button
                            type="button"
                            className="inline-flex h-7 items-center justify-center rounded border border-slate-300 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
                            data-testid={`customer-list-memo-open-${row.id}`}
                            disabled={isInactive}
                            onClick={() => openMemoModal(row)}
                          >
                            메모
                          </button>
                        </td>
                        <td className="px-1 py-2 text-center align-middle">
                          <Link
                            to={`/support/customers/${row.id}`}
                            className="inline-flex h-7 items-center justify-center rounded border border-slate-300 bg-white px-2.5 text-xs font-semibold text-brand-navy hover:bg-slate-50"
                          >
                            상세
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </div>
        {totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-sgez-border px-3 py-2 text-xs text-slate-600">
            <button
              type="button"
              className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40"
              disabled={page <= 0 || loading}
              onClick={() => {
                const np = Math.max(0, page - 1)
                loadList(np)
              }}
            >
              이전
            </button>
            <span>
              {page + 1} / {totalPages || 1}
            </span>
            <button
              type="button"
              className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40"
              disabled={loading || page + 1 >= totalPages}
              onClick={() => loadList(page + 1)}
            >
              다음
            </button>
          </div>
        ) : null}
        </WorkSurface>
      </div>

      {createOpen ? (
        <div className="fixed inset-0 z-[58]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={createBusy}
            onClick={() => { if (!createBusy) setCreateOpen(false) }}
            aria-label="고객 등록 닫기"
          />
          <div className="relative mx-auto max-w-6xl px-3 py-3 pointer-events-none sm:px-4 sm:py-8">
            <div
              className="pointer-events-auto flex max-h-[calc(100dvh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-sgez-border-strong bg-sgez-surface shadow-xl sm:max-h-[min(92vh,760px)]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="customer-create-title"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div id="customer-create-title" className="font-bold text-sm sm:text-base">고객 등록</div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    type="button"
                    variant="sgezOutline"
                    size="sm"
                    disabled={createBusy}
                    onClick={() => setCreateOpen(false)}
                  >
                    {"\uCDE8\uC18C"}
                  </Button>
                  <Button
                    type="button"
                    variant="sgez"
                    size="sm"
                    disabled={createBusy}
                    onClick={submitCreate}
                  >
                    {createBusy ? "\uB4F1\uB85D \uC911\u2026" : "\uB4F1\uB85D"}
                  </Button>
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 text-sm">
                <p className="mb-4 text-xs text-slate-500">
                  동일 연락처 고객이 이미 있으면 기존 고객 상세로 이동합니다. 고객 등록만으로 고객에게 알림이 전송되지는 않습니다.
                </p>
                <div className="grid gap-4 lg:grid-cols-2 lg:items-stretch">
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-3">
                      <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                      <div className="text-sm font-bold text-slate-900">기본 정보</div>
                    </div>
                    <div className="space-y-3 p-4">
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-600">이름 <RequiredMark /></span>
                        <input
                          className="w-full rounded border px-3 py-2 text-sm"
                          placeholder="이름"
                          value={createName}
                          onChange={(e) => setCreateName(e.target.value)}
                          disabled={createBusy}
                          required
                          aria-required="true"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-600">연락처 <RequiredMark /></span>
                        <input
                          className="w-full rounded border px-3 py-2 text-sm tabular-nums"
                          placeholder="010-0000-0000 (하이픈 자동)"
                          value={createPhone}
                          onChange={(e) => setCreatePhone(formatKoreanPhoneInput(e.target.value))}
                          disabled={createBusy}
                          inputMode="numeric"
                          autoComplete="tel"
                          required
                          aria-required="true"
                        />
                      </label>
                      <input
                        className="w-full rounded border px-3 py-2 text-sm tabular-nums"
                        placeholder="YYYY-MM-DD (하이픈 자동)"
                        value={createBirthDate}
                        onChange={(e) => setCreateBirthDate(formatBirthYmdInput(e.target.value))}
                        disabled={createBusy}
                        inputMode="numeric"
                        autoComplete="bday"
                      />
                      <p className="text-[11px] text-slate-500">
                        생년월일을 넣으면 상령일(생일 + 6개월)이 자동 계산되어 캘린더 알림에 사용됩니다.
                      </p>
                      <select
                        className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={createGender}
                        onChange={(e) => setCreateGender(e.target.value)}
                        disabled={createBusy}
                        aria-label="성별"
                      >
                        <option value="">성별 미입력</option>
                        <option value="MALE">남성</option>
                        <option value="FEMALE">여성</option>
                        <option value="UNKNOWN">확인 필요</option>
                      </select>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-0.5 block text-xs font-medium text-slate-600">고객 구분 (선택)</label>
                          <select
                            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={createCustomerSegment}
                            onChange={(e) => setCreateCustomerSegment(e.target.value)}
                            disabled={createBusy}
                          >
                            {createSegmentSelectOptions.map((o) => (
                              <option key={o.value === '' ? '_seg' : o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-0.5 block text-xs font-medium text-slate-600">고객 출처 (선택)</label>
                          <select
                            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={createCustomerSource}
                            onChange={(e) => setCreateCustomerSource(e.target.value)}
                            disabled={createBusy}
                          >
                            {createSourceSelectOptions.map((o) => (
                              <option key={o.value === '' ? '_src' : o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        placeholder="소속 (선택)"
                        value={createAffiliation}
                        onChange={(e) => setCreateAffiliation(e.target.value)}
                        maxLength={255}
                        disabled={createBusy}
                      />
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        placeholder="호칭 (선택)"
                        value={createHonorific}
                        onChange={(e) => setCreateHonorific(e.target.value)}
                        maxLength={80}
                        disabled={createBusy}
                      />
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        placeholder="직업·업종 (선택)"
                        value={createOccupation}
                        onChange={(e) => setCreateOccupation(e.target.value)}
                        maxLength={255}
                        disabled={createBusy}
                      />
                      <div className="grid gap-3 sm:grid-cols-3">
                        <label className="block sm:col-span-1">
                          <span className="mb-0.5 block text-xs font-medium text-slate-600">월 소득 (만원)</span>
                          <div className="flex rounded border border-slate-300 bg-white focus-within:ring-1 focus-within:ring-brand-navy">
                            <input
                              className="min-w-0 flex-1 rounded-l px-3 py-2 text-sm tabular-nums outline-none"
                              value={createMonthlyIncome}
                              onChange={(e) => setCreateMonthlyIncome(e.target.value.replace(/\D/g, ''))}
                              inputMode="numeric"
                              placeholder="예: 500"
                              maxLength={7}
                              disabled={createBusy}
                            />
                            <span className="shrink-0 border-l border-slate-200 bg-slate-50 px-2 py-2 text-xs font-semibold text-slate-500">만원</span>
                          </div>
                        </label>
                        <label className="block">
                          <span className="mb-0.5 block text-xs font-medium text-slate-600">확인 기준</span>
                          <select
                            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={createIncomeBasis}
                            onChange={(e) => setCreateIncomeBasis(e.target.value)}
                            disabled={createBusy}
                          >
                            <option value="">미입력</option>
                            <option value="SELF">본인</option>
                            <option value="HOUSEHOLD">가구</option>
                            <option value="ESTIMATED">추정</option>
                          </select>
                        </label>
                        <label className="block">
                          <span className="mb-0.5 block text-xs font-medium text-slate-600">맞벌이 여부</span>
                          <select
                            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={createDualIncomeStatus}
                            onChange={(e) => setCreateDualIncomeStatus(e.target.value)}
                            disabled={createBusy}
                          >
                            <option value="">미입력</option>
                            <option value="YES">맞벌이</option>
                            <option value="NO">외벌이</option>
                            <option value="UNKNOWN">미확인</option>
                          </select>
                        </label>
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <label className="text-xs font-semibold text-slate-600">집 주소 (선택)</label>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={createBusy}
                            onClick={() => openCreateAddressSearch('home')}
                          >
                            주소 검색
                          </button>
                        </div>
                        <input
                          type="text"
                          className="w-full rounded border px-3 py-2 text-sm"
                          placeholder="집 주소"
                          value={createHomeAddress}
                          onChange={(e) => setCreateHomeAddress(e.target.value)}
                          maxLength={500}
                          disabled={createBusy}
                        />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <label className="text-xs font-semibold text-slate-600">회사 주소 (선택)</label>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={createBusy}
                            onClick={() => openCreateAddressSearch('company')}
                          >
                            주소 검색
                          </button>
                        </div>
                        <input
                          type="text"
                          className="w-full rounded border px-3 py-2 text-sm"
                          placeholder="회사 주소"
                          value={createCompanyAddress}
                          onChange={(e) => setCreateCompanyAddress(e.target.value)}
                          maxLength={500}
                          disabled={createBusy}
                        />
                      </div>
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        placeholder="이메일 (선택)"
                        type="email"
                        value={createCustomerEmail}
                        onChange={(e) => setCreateCustomerEmail(e.target.value)}
                        maxLength={255}
                        autoComplete="email"
                        disabled={createBusy}
                      />
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-3">
                      <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                      <div className="text-sm font-bold text-slate-900">{"\uBD80\uAC00 \uC815\uBCF4"}</div>
                    </div>
                    <div className="space-y-5 p-4">                        <section>
                          <div className="mb-2 text-xs font-semibold text-slate-700">{"\uAE30\uB150\uC77C"}</div>
                          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_132px_auto]">
                            <input
                              className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm"
                              value={createAnniversaryTitle}
                              onChange={(e) => setCreateAnniversaryTitle(e.target.value)}
                              placeholder={"\uAE30\uB150\uC77C\uBA85 \uC608: \uACB0\uD63C\uAE30\uB150\uC77C"}
                              maxLength={120}
                              disabled={createBusy}
                            />
                            <CreateCustomerYmdInput
                              value={createAnniversaryDate}
                              onChange={setCreateAnniversaryDate}
                              placeholder="YYYY-MM-DD"
                              ariaLabel={"\uAE30\uB150\uC77C \uB0A0\uC9DC"}
                              disabled={createBusy}
                            />
                            <button
                              type="button"
                              className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                              onClick={addCreateAnniversary}
                              disabled={createBusy}
                            >
                              추가
                            </button>
                          </div>
                          {createAnniversaries.length > 0 ? (
                            <div className="mt-2 space-y-1.5">
                              {createAnniversaries.map((row, index) => (
                                <div key={`${row.title}-${row.anniversaryDate}-${index}`} className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
                                  <span className="min-w-0 truncate"><strong>{row.title}</strong> <span className="text-slate-500">{row.anniversaryDate}</span></span>
                                  <button
                                    type="button"
                                    className="shrink-0 text-rose-700 hover:underline"
                                    onClick={() => setCreateAnniversaries((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                                    disabled={createBusy}
                                  >
                                    삭제
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </section>

                        <section className="border-t border-slate-100 pt-4">
                          <div className="mb-2 text-xs font-semibold text-slate-700">{"\uAC00\uC871"}</div>
                          <div className="grid gap-2 sm:grid-cols-2">
                            <input
                              className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm"
                              value={createFamilyName}
                              onChange={(e) => setCreateFamilyName(e.target.value)}
                              placeholder="이름"
                              maxLength={120}
                              disabled={createBusy}
                            />
                            <input
                              className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm"
                              value={createFamilyRelationship}
                              onChange={(e) => setCreateFamilyRelationship(e.target.value)}
                              placeholder="관계 예: 배우자, 자녀"
                              maxLength={80}
                              disabled={createBusy}
                            />
                            <CreateCustomerYmdInput
                              value={createFamilyBirthDate}
                              onChange={setCreateFamilyBirthDate}
                              placeholder={"\uC0DD\uB144\uC6D4\uC77C YYYY-MM-DD"}
                              ariaLabel={"\uAC00\uC871 \uC0DD\uB144\uC6D4\uC77C"}
                              disabled={createBusy}
                            />
                            <select
                              className="min-w-0 rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                              value={createFamilyCohabiting}
                              onChange={(e) => setCreateFamilyCohabiting(e.target.value)}
                              disabled={createBusy}
                            >
                              <option value="">동거 여부 미확인</option>
                              <option value="YES">동거</option>
                              <option value="NO">비동거</option>
                            </select>
                          </div>
                          <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                            <input
                              className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm"
                              value={createFamilyLinkedSearch}
                              onChange={(e) => setCreateFamilyLinkedSearch(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  searchCreateFamilyLinkCandidates()
                                }
                              }}
                              placeholder={"\uAE30\uC874 \uACE0\uAC1D \uC5F0\uACB0 \uAC80\uC0C9"}
                              disabled={createBusy}
                            />
                            <button
                              type="button"
                              className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                              onClick={searchCreateFamilyLinkCandidates}
                              disabled={createBusy}
                            >
                              검색
                            </button>
                          </div>
                          {createFamilyLinkCandidates.length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {createFamilyLinkCandidates.map((item) => (
                                <button
                                  key={item.id}
                                  type="button"
                                  className={`max-w-full truncate rounded border px-2 py-1 text-[11px] font-semibold ${String(createFamilyLinkedCustomerRefId) === String(item.id) ? 'border-brand-navy bg-brand-navy text-white' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
                                  onClick={() => {
                                    setCreateFamilyLinkedCustomerRefId(String(item.id))
                                    setCreateFamilyName(item.customerName || createFamilyName)
                                    setCreateFamilyBirthDate(formatBirthYmdInput(item.birthDate || createFamilyBirthDate))
                                  }}
                                  disabled={createBusy}
                                >
                                  {customerNamePhoneLabel(item)}
                                </button>
                              ))}
                            </div>
                          ) : null}
                          {createFamilyLinkedCustomerRefId ? (
                            <div className="mt-2 flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-700">
                              <span>기존 고객 연결을 선택했습니다.</span>
                              <button type="button" className="font-semibold text-slate-700 hover:underline" onClick={() => setCreateFamilyLinkedCustomerRefId('')} disabled={createBusy}>해제</button>
                            </div>
                          ) : null}
                          <textarea
                            className="mt-2 min-h-[64px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
                            value={createFamilyMemo}
                            onChange={(e) => setCreateFamilyMemo(e.target.value)}
                            placeholder="가족 메모"
                            maxLength={1000}
                            disabled={createBusy}
                          />
                          <div className="mt-2 flex justify-end">
                            <button
                              type="button"
                              className="rounded bg-brand-navy px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                              onClick={addCreateFamilyMember}
                              disabled={createBusy}
                            >
                              가족 추가
                            </button>
                          </div>
                          {createFamilyMembers.length > 0 ? (
                            <div className="mt-2 space-y-1.5">
                              {createFamilyMembers.map((row, index) => (
                                <div key={`${row.name}-${row.relationship}-${index}`} className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
                                  <span className="min-w-0 truncate"><strong>{row.name || '연결 고객'}</strong>{row.relationship ? ` · ${row.relationship}` : ''}{row.birthDate ? ` · ${row.birthDate}` : ''}</span>
                                  <button
                                    type="button"
                                    className="shrink-0 text-rose-700 hover:underline"
                                    onClick={() => setCreateFamilyMembers((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                                    disabled={createBusy}
                                  >
                                    삭제
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </section>
                      <section className="border-t border-slate-100 pt-4">
                        <div className="text-xs font-semibold text-slate-700">{"\uBA54\uBAA8"}</div>
                        <div className="mt-2">                        <div className="rounded-md border border-amber-200 bg-amber-50/90 px-2.5 py-1.5 text-[10px] leading-snug text-amber-950">
                          주민번호·계좌·카드·건강 진단 등 민감정보는 적지 마세요.
                        </div>
                        <textarea
                          className="mt-1.5 w-full min-h-[120px] rounded border px-3 py-2 text-sm"
                          placeholder={"\uC0C1\uB2F4 \uBA54\uBAA8 \uB4F1"}
                          value={createMemo}
                          onChange={(e) => setCreateMemo(e.target.value)}
                          maxLength={4000}
                          disabled={createBusy}
                        />
                        <div className="mt-0.5 text-[10px] text-slate-500">{createMemo.length} / 4000</div>
                        </div>
                      </section>

                      <section className="border-t border-slate-100 pt-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-xs font-semibold text-slate-700">{"\uBA85\uD568"}</div>
                          <Button
                            type="button"
                            variant="sgezOutline"
                            size="sm"
                            className="shrink-0"
                            disabled={createBusy}
                            onClick={openCreateBizWizard}
                          >
                            {"\uBA85\uD568 \uC5C5\uB85C\uB4DC"}
                          </Button>
                        </div>
                        <div className="mt-2">                        {createPendingBizFrontId ? (
                          <p className="text-xs text-emerald-800">등록 시 명함 이미지가 새 고객에 연결됩니다.</p>
                        ) : (
                          <p className="text-xs text-slate-500">명함 업로드 시 명함 정보가 기본 정보 필드로 자동 입력됩니다.</p>
                        )}
                        </div>
                      </section>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {createOpen && createBizWizardOpen ? (
        <div className="fixed inset-0 z-[60]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={createBizWizardBusy}
            onClick={() => { if (!createBizWizardBusy) setCreateBizWizardOpen(false) }}
            aria-label="명함 업로드 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto relative rounded-2xl bg-white shadow-xl border overflow-hidden max-h-[min(90vh,640px)] flex flex-col"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5 shrink-0">
                <div className="font-bold text-sm sm:text-base">
                  {createBizWizardStep === 'front' ? '명함 앞면 업로드' : '명함 뒷면 업로드'}
                </div>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                  disabled={createBizWizardBusy}
                  onClick={() => setCreateBizWizardOpen(false)}
                >
                  닫기
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 text-sm">
                {createBizWizardStep === 'front' ? (
                  <>
                    <p className="text-xs text-slate-500">
                      앞면을 올린 뒤「다음」에서 뒷면을 추가하거나, 뒷면 없이 바로 인식할 수 있습니다.
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={createBizWizardBusy}
                      onChange={async (e) => {
                        const f = e.target.files?.[0]
                        e.target.value = ''
                        if (!f) return
                        setCreateBizWizardBusy(true)
                        try {
                          const fid = await uploadCreateBizCardFile(f)
                          setCreateBizDraftFrontId(fid)
                          try { window?.toast?.show?.('앞면이 업로드되었습니다.', 'success') } catch { }
                        } catch (upErr) {
                          const um = getApiErrorMessage(upErr, '업로드에 실패했습니다.')
                          setErr(um)
                          try { window?.toast?.show?.(um, 'error', 4000) } catch { }
                        } finally {
                          setCreateBizWizardBusy(false)
                        }
                      }}
                      className="block w-full text-xs"
                    />
                    {createBizDraftFrontId ? (
                      <p className="text-[11px] text-emerald-700">앞면 파일이 준비되었습니다.</p>
                    ) : null}
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                        disabled={createBizWizardBusy}
                        onClick={() => setCreateBizWizardOpen(false)}
                      >
                        취소
                      </button>
                      <button
                        type="button"
                        className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                        disabled={createBizWizardBusy || !createBizDraftFrontId}
                        onClick={() => setCreateBizWizardStep('back')}
                      >
                        다음
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-500">뒷면이 없으면 건너뛰고 인식만 진행할 수 있습니다.</p>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={createBizWizardBusy}
                      onChange={async (e) => {
                        const f = e.target.files?.[0]
                        e.target.value = ''
                        if (!f) return
                        setCreateBizWizardBusy(true)
                        try {
                          const fid = await uploadCreateBizCardFile(f)
                          setCreateBizDraftBackId(fid)
                          try { window?.toast?.show?.('뒷면이 업로드되었습니다.', 'success') } catch { }
                        } catch (upErr) {
                          const um = getApiErrorMessage(upErr, '업로드에 실패했습니다.')
                          setErr(um)
                          try { window?.toast?.show?.(um, 'error', 4000) } catch { }
                        } finally {
                          setCreateBizWizardBusy(false)
                        }
                      }}
                      className="block w-full text-xs"
                    />
                    {createBizDraftBackId ? (
                      <p className="text-[11px] text-emerald-700">뒷면 파일이 준비되었습니다.</p>
                    ) : null}
                    <div className="flex justify-between gap-2 pt-2">
                      <button
                        type="button"
                        className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                        disabled={createBizWizardBusy}
                        onClick={() => setCreateBizWizardStep('front')}
                      >
                        이전
                      </button>
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                          disabled={createBizWizardBusy}
                          onClick={runCreateBizCardExtractAndFill}
                        >
                          뒷면 없이 완료
                        </button>
                        <button
                          type="button"
                          className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                          disabled={createBizWizardBusy}
                          onClick={runCreateBizCardExtractAndFill}
                        >
                          완료
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {(createBizWizardBusy || createBizExtracting) ? (
                <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center bg-white/80 px-4 backdrop-blur-[1px]">
                  <span
                    className="inline-block h-9 w-9 animate-spin rounded-full border-2 border-slate-200 border-t-brand-navy"
                    aria-hidden
                  />
                  <p className="mt-3 max-w-[17rem] text-center text-sm font-medium text-slate-800">
                    {createBizExtracting ? '명함에서 정보를 읽는 중입니다. 잠시만 기다려 주세요.' : '처리 중입니다…'}
                  </p>
                </div>
              ) : null}
          </div>
        </div>
        </div>
      ) : null}

      <CustomerListFilterDialog
        open={filterOpen}
        filters={customerFilters}
        options={customerFilterOptions}
        onApply={setCustomerFilters}
        onReset={() => setCustomerFilters({ ...EMPTY_CUSTOMER_LIST_FILTERS })}
        onClose={() => setFilterOpen(false)}
      />

      {memoModal ? (
        <div className="fixed inset-0 z-[59]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={memoSaving}
            onClick={() => { if (!memoSaving) setMemoModal(null) }}
            aria-label="메모 닫기"
          />
          <div className="relative mx-auto max-w-5xl px-3 py-6 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto rounded-2xl bg-white shadow-xl border overflow-hidden flex max-h-[min(92vh,780px)] flex-col"
              data-testid="customer-list-memo-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="customer-memo-list-title"
            >
              <div className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div id="customer-memo-list-title" className="font-bold text-sm sm:text-base">
                  고객 메모 · {memoModal.name}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {memoModal.rich ? (
                    <button
                      type="button"
                      className="rounded bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                      disabled={memoSaving}
                      data-testid="customer-list-memo-save"
                      onClick={saveMemoFromList}
                    >
                      {memoSaving ? '저장 중…' : '저장'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                    disabled={memoSaving}
                    onClick={() => setMemoModal(null)}
                  >
                    닫기
                  </button>
                </div>
              </div>
              <div className="min-h-0 flex flex-1 flex-col p-4 sm:p-5 text-sm">
                {memoModal.rich ? (
                  <>
                    {memoEditorMode === 'detail' ? (
                      <CustomerMemoTemplatePicker
                        templates={memoTemplates}
                        disabled={memoSaving}
                        onApply={applyMemoTemplate}
                      />
                    ) : null}
                    {memoEditorMode === 'detail' ? (
                      <>
                        <input
                          type="text"
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                          value={memoEditorTitle}
                          onChange={(e) => setMemoEditorTitle(e.target.value)}
                          maxLength={255}
                          placeholder="제목"
                          disabled={memoSaving}
                        />
                        <div className="grid gap-2 sm:grid-cols-2">
                          <input
                            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                            value={memoCategory}
                            onChange={(e) => setMemoCategory(e.target.value)}
                            placeholder="분류"
                            disabled={memoSaving}
                          />
                          <input
                            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                            value={memoTagsText}
                            onChange={(e) => setMemoTagsText(e.target.value)}
                            placeholder="태그"
                            disabled={memoSaving}
                          />
                        </div>
                      </>
                    ) : null}
                    <CustomerMemoRichEditor
                      className="min-h-0 flex flex-1 flex-col"
                      shellClassName="customer-memo-tiptap-scroll min-h-0 flex flex-1 flex-col"
                      contentClassName="customer-memo-editor-content min-h-0 flex-1 overflow-y-auto"
                      uploadPath="/files/upload/customer-memo"
                      valueHtml={memoContentToHtml(memoEditorContent, memoDraft)}
                      plainText={memoDraft}
                      attachments={memoAttachments}
                      disabled={memoSaving}
                      placeholder={memoEditorMode === 'simple' ? '첫 줄 제목, #태그' : '상담 메모, 링크, 참고 내용을 입력하세요'}
                      toolbarMode="compact"
                      onChange={(next) => {
                        setMemoDraft(next.plainText)
                        setMemoEditorContent(next.content)
                        if (!memoSaving) setMemoDraftStatus('작성 중')
                      }}
                      onAttachmentsChange={(next) => {
                        setMemoAttachments(next)
                        if (!memoSaving) setMemoDraftStatus('작성 중')
                      }}
                    />
                    {memoEditorMode === 'detail' ? (
                      <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300"
                          checked={memoPinned}
                          disabled={memoSaving}
                          onChange={(e) => setMemoPinned(e.target.checked)}
                        />
                        고정
                      </label>
                    ) : null}
                    <div className="flex flex-wrap items-center justify-end gap-2 text-[11px] text-slate-500">
                      {memoDraftStatus ? <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-600">{memoDraftStatus}</span> : null}
                      <span>{memoDraft.length.toLocaleString('ko-KR')} / 50,000</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-md border border-amber-200 bg-amber-50/90 px-3 py-2 text-[11px] leading-snug text-amber-950">
                      주민등록번호, 계좌번호, 카드번호, 건강·진단 상세 등 <strong>민감한 개인정보는 이 메모에 적지 마세요.</strong> 분실·유출 시 법적 책임이 발생할 수 있습니다.
                    </div>
                    <textarea
                      className="w-full min-h-[140px] rounded border border-slate-300 px-3 py-2 text-sm"
                      value={memoDraft}
                      onChange={(e) => setMemoDraft(e.target.value)}
                      maxLength={4000}
                      placeholder="상담 메모 등 (민감정보 제외)"
                      disabled={memoSaving}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                      <span>{memoDraft.length} / 4000</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                          disabled={memoSaving}
                          onClick={() => setMemoModal(null)}
                        >
                          취소
                        </button>
                        <button
                          type="button"
                          className="rounded bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                          disabled={memoSaving}
                          onClick={saveMemoFromList}
                        >
                          {memoSaving ? '저장 중…' : '저장'}
                        </button>
                      </div>
                    </div>
                    <Link className="inline-block text-xs font-medium text-brand-navy hover:underline" to={`/support/customers/${memoModal.id}`}>
                      상세 페이지에서 더 보기 →
                    </Link>
                  </>
                )}
              </div>
          </div>
        </div>
        </div>
      ) : null}

      {schedModal ? (
        <div className="fixed inset-0 z-[59]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={schedBusy}
            onClick={() => { if (!schedBusy) setSchedModal(null) }}
            aria-label="일정 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto flex max-h-[calc(100dvh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-sgez-border-strong bg-sgez-surface shadow-xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="customer-sched-title"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div id="customer-sched-title" className="font-bold text-sm sm:text-base">
                  일정 등록 · {schedModal.name}
                </div>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                  disabled={schedBusy}
                  onClick={() => setSchedModal(null)}
                >
                  닫기
                </button>
              </div>
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 text-sm sm:p-5">
                <p className="text-xs text-slate-500">
                  내 개인 일정으로 등록되며, 이 고객과 연결됩니다.{' '}
                  <Link className="font-medium text-brand-navy hover:underline" to="/work/calendar">업무 캘린더</Link>에서 확인할 수 있습니다.
                </p>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">제목</label>
                  <input
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={schedTitle}
                    onChange={(e) => setSchedTitle(e.target.value)}
                    disabled={schedBusy}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">설명 (선택)</label>
                  <textarea
                    className="w-full min-h-[72px] rounded border px-3 py-2 text-sm"
                    value={schedDesc}
                    onChange={(e) => setSchedDesc(e.target.value)}
                    disabled={schedBusy}
                    placeholder="통화 목적, 준비물 등"
                  />
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-0.5">시작</label>
                    <input
                      type="datetime-local"
                      className="w-full rounded border px-2 py-2 text-sm"
                      value={schedStart}
                      onChange={(e) => setSchedStart(e.target.value)}
                      disabled={schedBusy}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-0.5">종료</label>
                    <input
                      type="datetime-local"
                      className="w-full rounded border px-2 py-2 text-sm"
                      value={schedEnd}
                      onChange={(e) => setSchedEnd(e.target.value)}
                      disabled={schedBusy}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    className="rounded border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                    disabled={schedBusy}
                    onClick={() => setSchedModal(null)}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                    disabled={schedBusy}
                    onClick={submitSchedule}
                  >
                    {schedBusy ? '등록 중…' : '일정 등록'}
                  </button>
                </div>
              </div>
          </div>
        </div>
        </div>
      ) : null}

      {contactModal ? (
        <div className="fixed inset-0 z-[59]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={contactBusy}
            onClick={() => { if (!contactBusy) setContactModal(null) }}
            aria-label="연락 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto flex max-h-[calc(100dvh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-sgez-border-strong bg-sgez-surface shadow-xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="customer-contact-title"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div id="customer-contact-title" className="font-bold text-sm sm:text-base">
                  연락 등록 · {contactModal.name}
                </div>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                  disabled={contactBusy}
                  onClick={() => setContactModal(null)}
                >
                  닫기
                </button>
              </div>
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 text-sm sm:p-5">
                <p className="text-xs text-slate-500">
                  연락 이력은 고객과 연결된 개인 일정으로 함께 저장되어 <Link className="font-medium text-brand-navy hover:underline" to="/work/calendar">일정 메뉴</Link>에서도 확인할 수 있습니다.
                </p>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">연락 일시</label>
                  <input
                    type="datetime-local"
                    className="w-full rounded border px-2 py-2 text-sm"
                    value={contactAt}
                    onChange={(e) => setContactAt(e.target.value)}
                    disabled={contactBusy}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">연락 내용</label>
                  <textarea
                    className="w-full min-h-[120px] rounded border px-3 py-2 text-sm"
                    value={contactContent}
                    onChange={(e) => setContactContent(e.target.value)}
                    disabled={contactBusy}
                    maxLength={2000}
                    placeholder="통화/문자/방문 등 연락 내용과 후속 조치를 간략히 적어 주세요."
                  />
                  <div className="mt-1 text-right text-[11px] text-slate-400">{contactContent.length}/2000</div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    className="rounded border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                    disabled={contactBusy}
                    onClick={() => setContactModal(null)}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                    disabled={contactBusy}
                    onClick={submitContact}
                  >
                    {contactBusy ? '등록 중…' : '연락 등록'}
                  </button>
                </div>
              </div>
          </div>
        </div>
        </div>
      ) : null}
    </PageFrame>
  )
}
